// Shared React hooks (deduplicated from per-file destructuring).
var { useState, useEffect, useRef, useMemo, useCallback, useReducer, useLayoutEffect } = React;


/* ── tweaks-panel.jsx ─────────────────────────────────────────── */


// tweaks-panel.jsx
// Reusable Tweaks shell + form-control helpers.
//
// Owns the host protocol (listens for __activate_edit_mode / __deactivate_edit_mode,
// posts __edit_mode_available / __edit_mode_set_keys / __edit_mode_dismissed) so
// individual prototypes don't re-roll it. Ships a consistent set of controls so you
// don't hand-draw <input type="range">, segmented radios, steppers, etc.
//
// Usage (in an HTML file that loads React + Babel):
//
//   const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
//     "primaryColor": "#D97757",
//     "palette": ["#D97757", "#29261b", "#f6f4ef"],
//     "fontSize": 16,
//     "density": "regular",
//     "dark": false
//   }/*EDITMODE-END*/;
//
//   function App() {
//     const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
//     return (
//       <div style={{ fontSize: t.fontSize, color: t.primaryColor }}>
//         Hello
//         <TweaksPanel>
//           <TweakSection label="Typography" />
//           <TweakSlider label="Font size" value={t.fontSize} min={10} max={32} unit="px"
//                        onChange={(v) => setTweak('fontSize', v)} />
//           <TweakRadio  label="Density" value={t.density}
//                        options={['compact', 'regular', 'comfy']}
//                        onChange={(v) => setTweak('density', v)} />
//           <TweakSection label="Theme" />
//           <TweakColor  label="Primary" value={t.primaryColor}
//                        options={['#D97757', '#2A6FDB', '#1F8A5B', '#7A5AE0']}
//                        onChange={(v) => setTweak('primaryColor', v)} />
//           <TweakColor  label="Palette" value={t.palette}
//                        options={[['#D97757', '#29261b', '#f6f4ef'],
//                                  ['#475569', '#0f172a', '#f1f5f9']]}
//                        onChange={(v) => setTweak('palette', v)} />
//           <TweakToggle label="Dark mode" value={t.dark}
//                        onChange={(v) => setTweak('dark', v)} />
//         </TweaksPanel>
//       </div>
//     );
//   }
//
// ─────────────────────────────────────────────────────────────────────────────

const __TWEAKS_STYLE = `
  .twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    transform:scale(var(--dc-inv-zoom,1));transform-origin:bottom right;
    background:rgba(250,249,247,.78);color:#29261b;
    -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
    border:.5px solid rgba(255,255,255,.6);border-radius:14px;
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
    font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:10px 8px 10px 14px;cursor:move;user-select:none}
  .twk-hd b{font-size:12px;font-weight:600;letter-spacing:.01em}
  .twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
    width:22px;height:22px;border-radius:6px;cursor:default;font-size:13px;line-height:1}
  .twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
  .twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
    overflow-y:auto;overflow-x:hidden;min-height:0;
    scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.15) transparent}
  .twk-body::-webkit-scrollbar{width:8px}
  .twk-body::-webkit-scrollbar-track{background:transparent;margin:2px}
  .twk-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px;
    border:2px solid transparent;background-clip:content-box}
  .twk-body::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.25);
    border:2px solid transparent;background-clip:content-box}
  .twk-row{display:flex;flex-direction:column;gap:5px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:rgba(41,38,27,.72)}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-val{color:rgba(41,38,27,.5);font-variant-numeric:tabular-nums}

  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:rgba(41,38,27,.45);padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}

  .twk-field{appearance:none;box-sizing:border-box;width:100%;min-width:0;height:26px;padding:0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;
    background:rgba(255,255,255,.6);color:inherit;font:inherit;outline:none}
  .twk-field:focus{border-color:rgba(0,0,0,.25);background:rgba(255,255,255,.85)}
  select.twk-field{padding-right:22px;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='rgba(0,0,0,.5)' d='M0 0h10L5 6z'/></svg>");
    background-repeat:no-repeat;background-position:right 8px center}

  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:6px 0;
    border-radius:999px;background:rgba(0,0,0,.12);outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
    width:14px;height:14px;border-radius:50%;background:#fff;
    border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}
  .twk-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;
    background:#fff;border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}

  .twk-seg{position:relative;display:flex;padding:2px;border-radius:8px;
    background:rgba(0,0,0,.06);user-select:none}
  .twk-seg-thumb{position:absolute;top:2px;bottom:2px;border-radius:6px;
    background:rgba(255,255,255,.9);box-shadow:0 1px 2px rgba(0,0,0,.12);
    transition:left .15s cubic-bezier(.3,.7,.4,1),width .15s}
  .twk-seg.dragging .twk-seg-thumb{transition:none}
  .twk-seg button{appearance:none;position:relative;z-index:1;flex:1;border:0;
    background:transparent;color:inherit;font:inherit;font-weight:500;min-height:22px;
    border-radius:6px;cursor:default;padding:4px 6px;line-height:1.2;
    overflow-wrap:anywhere}

  .twk-toggle{position:relative;width:32px;height:18px;border:0;border-radius:999px;
    background:rgba(0,0,0,.15);transition:background .15s;cursor:default;padding:0}
  .twk-toggle[data-on="1"]{background:#34c759}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;
    background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(14px)}

  .twk-num{display:flex;align-items:center;box-sizing:border-box;min-width:0;height:26px;padding:0 0 0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;background:rgba(255,255,255,.6)}
  .twk-num-lbl{font-weight:500;color:rgba(41,38,27,.6);cursor:ew-resize;
    user-select:none;padding-right:8px}
  .twk-num input{flex:1;min-width:0;height:100%;border:0;background:transparent;
    font:inherit;font-variant-numeric:tabular-nums;text-align:right;padding:0 8px 0 0;
    outline:none;color:inherit;-moz-appearance:textfield}
  .twk-num input::-webkit-inner-spin-button,.twk-num input::-webkit-outer-spin-button{
    -webkit-appearance:none;margin:0}
  .twk-num-unit{padding-right:8px;color:rgba(41,38,27,.45)}

  .twk-btn{appearance:none;height:26px;padding:0 12px;border:0;border-radius:7px;
    background:rgba(0,0,0,.78);color:#fff;font:inherit;font-weight:500;cursor:default}
  .twk-btn:hover{background:rgba(0,0,0,.88)}
  .twk-btn.secondary{background:rgba(0,0,0,.06);color:inherit}
  .twk-btn.secondary:hover{background:rgba(0,0,0,.1)}

  .twk-swatch{appearance:none;-webkit-appearance:none;width:56px;height:22px;
    border:.5px solid rgba(0,0,0,.1);border-radius:6px;padding:0;cursor:default;
    background:transparent;flex-shrink:0}
  .twk-swatch::-webkit-color-swatch-wrapper{padding:0}
  .twk-swatch::-webkit-color-swatch{border:0;border-radius:5.5px}
  .twk-swatch::-moz-color-swatch{border:0;border-radius:5.5px}

  .twk-chips{display:flex;gap:6px}
  .twk-chip{position:relative;appearance:none;flex:1;min-width:0;height:46px;
    padding:0;border:0;border-radius:6px;overflow:hidden;cursor:default;
    box-shadow:0 0 0 .5px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.06);
    transition:transform .12s cubic-bezier(.3,.7,.4,1),box-shadow .12s}
  .twk-chip:hover{transform:translateY(-1px);
    box-shadow:0 0 0 .5px rgba(0,0,0,.18),0 4px 10px rgba(0,0,0,.12)}
  .twk-chip[data-on="1"]{box-shadow:0 0 0 1.5px rgba(0,0,0,.85),
    0 2px 6px rgba(0,0,0,.15)}
  .twk-chip>span{position:absolute;top:0;bottom:0;right:0;width:34%;
    display:flex;flex-direction:column;box-shadow:-1px 0 0 rgba(0,0,0,.1)}
  .twk-chip>span>i{flex:1;box-shadow:0 -1px 0 rgba(0,0,0,.1)}
  .twk-chip>span>i:first-child{box-shadow:none}
  .twk-chip svg{position:absolute;top:6px;left:6px;width:13px;height:13px;
    filter:drop-shadow(0 1px 1px rgba(0,0,0,.3))}
`;

// ── useTweaks ───────────────────────────────────────────────────────────────
// Single source of truth for tweak values. setTweak persists via the host
// (__edit_mode_set_keys → host rewrites the EDITMODE block on disk).
function useTweaks(defaults) {
  const [values, setValues] = React.useState(defaults);
  // Accepts either setTweak('key', value) or setTweak({ key: value, ... }) so a
  // useState-style call doesn't write a "[object Object]" key into the persisted
  // JSON block.
  const setTweak = React.useCallback((keyOrEdits, val) => {
    const edits = typeof keyOrEdits === 'object' && keyOrEdits !== null
      ? keyOrEdits : { [keyOrEdits]: val };
    setValues((prev) => ({ ...prev, ...edits }));
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits }, '*');
    // Same-window signal so in-page listeners (deck-stage rail thumbnails)
    // can react — the parent message only reaches the host, not peers.
    window.dispatchEvent(new CustomEvent('tweakchange', { detail: edits }));
  }, []);
  return [values, setTweak];
}

// ── TweaksPanel ─────────────────────────────────────────────────────────────
// Floating shell. Registers the protocol listener BEFORE announcing
// availability — if the announce ran first, the host's activate could land
// before our handler exists and the toolbar toggle would silently no-op.
// The close button posts __edit_mode_dismissed so the host's toolbar toggle
// flips off in lockstep; the host echoes __deactivate_edit_mode back which
// is what actually hides the panel.
function TweaksPanel({ title = 'Tweaks', children }) {
  const [open, setOpen] = React.useState(false);
  const dragRef = React.useRef(null);
  const offsetRef = React.useRef({ x: 16, y: 16 });
  const PAD = 16;

  const clampToViewport = React.useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth, h = panel.offsetHeight;
    const maxRight = Math.max(PAD, window.innerWidth - w - PAD);
    const maxBottom = Math.max(PAD, window.innerHeight - h - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxBottom, Math.max(PAD, offsetRef.current.y)),
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.bottom = offsetRef.current.y + 'px';
  }, []);

  React.useEffect(() => {
    if (!open) return;
    clampToViewport();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', clampToViewport);
      return () => window.removeEventListener('resize', clampToViewport);
    }
    const ro = new ResizeObserver(clampToViewport);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [open, clampToViewport]);

  React.useEffect(() => {
    const onMsg = (e) => {
      const t = e?.data?.type;
      if (t === '__activate_edit_mode') setOpen(true);
      else if (t === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*');
  };

  const onDragStart = (e) => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startBottom = window.innerHeight - r.bottom;
    const move = (ev) => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startBottom - (ev.clientY - sy),
      };
      clampToViewport();
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) return null;
  return (
    <>
      <style>{__TWEAKS_STYLE}</style>
      <div ref={dragRef} className="twk-panel" data-omelette-chrome=""
           style={{ right: offsetRef.current.x, bottom: offsetRef.current.y }}>
        <div className="twk-hd" onMouseDown={onDragStart}>
          <b>{title}</b>
          <button className="twk-x" aria-label="Close tweaks"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={dismiss}>✕</button>
        </div>
        <div className="twk-body">
          {children}
        </div>
      </div>
    </>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────

function TweakSection({ label, children }) {
  return (
    <>
      <div className="twk-sect">{label}</div>
      {children}
    </>
  );
}

function TweakRow({ label, value, children, inline = false }) {
  return (
    <div className={inline ? 'twk-row twk-row-h' : 'twk-row'}>
      <div className="twk-lbl">
        <span>{label}</span>
        {value != null && <span className="twk-val">{value}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

function TweakSlider({ label, value, min = 0, max = 100, step = 1, unit = '', onChange }) {
  return (
    <TweakRow label={label} value={`${value}${unit}`}>
      <input type="range" className="twk-slider" min={min} max={max} step={step}
             value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </TweakRow>
  );
}

function TweakToggle({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <button type="button" className="twk-toggle" data-on={value ? '1' : '0'}
              role="switch" aria-checked={!!value}
              onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}

function TweakRadio({ label, value, options, onChange }) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  // The active value is read by pointer-move handlers attached for the lifetime
  // of a drag — ref it so a stale closure doesn't fire onChange for every move.
  const valueRef = React.useRef(value);
  valueRef.current = value;

  // Segments wrap mid-word once per-segment width runs out. The track is
  // ~248px (280 panel − 28 body pad − 4 seg pad), each button loses 12px
  // to its own padding, and 11.5px system-ui averages ~6.3px/char — so 2
  // options fit ~16 chars each, 3 fit ~10. Past that (or >3 options), fall
  // back to a dropdown rather than wrap.
  const labelLen = (o) => String(typeof o === 'object' ? o.label : o).length;
  const maxLen = options.reduce((m, o) => Math.max(m, labelLen(o)), 0);
  const fitsAsSegments = maxLen <= ({ 2: 16, 3: 10 }[options.length] ?? 0);
  if (!fitsAsSegments) {
    // <select> emits strings — map back to the original option value so the
    // fallback stays type-preserving (numbers, booleans) like the segment path.
    const resolve = (s) => {
      const m = options.find((o) => String(typeof o === 'object' ? o.value : o) === s);
      return m === undefined ? s : typeof m === 'object' ? m.value : m;
    };
    return <TweakSelect label={label} value={value} options={options}
                        onChange={(s) => onChange(resolve(s))} />;
  }
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const idx = Math.max(0, opts.findIndex((o) => o.value === value));
  const n = opts.length;

  const segAt = (clientX) => {
    const r = trackRef.current.getBoundingClientRect();
    const inner = r.width - 4;
    const i = Math.floor(((clientX - r.left - 2) / inner) * n);
    return opts[Math.max(0, Math.min(n - 1, i))].value;
  };

  const onPointerDown = (e) => {
    setDragging(true);
    const v0 = segAt(e.clientX);
    if (v0 !== valueRef.current) onChange(v0);
    const move = (ev) => {
      if (!trackRef.current) return;
      const v = segAt(ev.clientX);
      if (v !== valueRef.current) onChange(v);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <TweakRow label={label}>
      <div ref={trackRef} role="radiogroup" onPointerDown={onPointerDown}
           className={dragging ? 'twk-seg dragging' : 'twk-seg'}>
        <div className="twk-seg-thumb"
             style={{ left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
                      width: `calc((100% - 4px) / ${n})` }} />
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value}>
            {o.label}
          </button>
        ))}
      </div>
    </TweakRow>
  );
}

function TweakSelect({ label, value, options, onChange }) {
  return (
    <TweakRow label={label}>
      <select className="twk-field" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const v = typeof o === 'object' ? o.value : o;
          const l = typeof o === 'object' ? o.label : o;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </TweakRow>
  );
}

function TweakText({ label, value, placeholder, onChange }) {
  return (
    <TweakRow label={label}>
      <input className="twk-field" type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </TweakRow>
  );
}

function TweakNumber({ label, value, min, max, step = 1, unit = '', onChange }) {
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const startRef = React.useRef({ x: 0, val: 0 });
  const onScrubStart = (e) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, val: value };
    const decimals = (String(step).split('.')[1] || '').length;
    const move = (ev) => {
      const dx = ev.clientX - startRef.current.x;
      const raw = startRef.current.val + dx * step;
      const snapped = Math.round(raw / step) * step;
      onChange(clamp(Number(snapped.toFixed(decimals))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="twk-num">
      <span className="twk-num-lbl" onPointerDown={onScrubStart}>{label}</span>
      <input type="number" value={value} min={min} max={max} step={step}
             onChange={(e) => onChange(clamp(Number(e.target.value)))} />
      {unit && <span className="twk-num-unit">{unit}</span>}
    </div>
  );
}

// Relative-luminance contrast pick — checkmarks drawn over a swatch need to
// read on both #111 and #fafafa without per-option configuration. Hex input
// only (#rgb / #rrggbb); named or rgb()/hsl() colors fall through to "light".
function __twkIsLight(hex) {
  const h = String(hex).replace('#', '');
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, '0');
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return true;
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000;
}

const __TwkCheck = ({ light }) => (
  <svg viewBox="0 0 14 14" aria-hidden="true">
    <path d="M3 7.2 5.8 10 11 4.2" fill="none" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round"
          stroke={light ? 'rgba(0,0,0,.78)' : '#fff'} />
  </svg>
);

// TweakColor — curated color/palette picker. Each option is either a single
// hex string or an array of 1-5 hex strings; the card adapts — a lone color
// renders solid, a palette renders colors[0] as the hero (left ~2/3) with the
// rest stacked in a sharp column on the right. onChange emits the
// option in the shape it was passed (string stays string, array stays array).
// Without options it falls back to the native color input for back-compat.
function TweakColor({ label, value, options, onChange }) {
  if (!options || !options.length) {
    return (
      <div className="twk-row twk-row-h">
        <div className="twk-lbl"><span>{label}</span></div>
        <input type="color" className="twk-swatch" value={value}
               onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  // Native <input type=color> emits lowercase hex per the HTML spec, so
  // compare case-insensitively. String() guards JSON.stringify(undefined),
  // which returns the primitive undefined (no .toLowerCase).
  const key = (o) => String(JSON.stringify(o)).toLowerCase();
  const cur = key(value);
  return (
    <TweakRow label={label}>
      <div className="twk-chips" role="radiogroup">
        {options.map((o, i) => {
          const colors = Array.isArray(o) ? o : [o];
          const [hero, ...rest] = colors;
          const sup = rest.slice(0, 4);
          const on = key(o) === cur;
          return (
            <button key={i} type="button" className="twk-chip" role="radio"
                    aria-checked={on} data-on={on ? '1' : '0'}
                    aria-label={colors.join(', ')} title={colors.join(' · ')}
                    style={{ background: hero }}
                    onClick={() => onChange(o)}>
              {sup.length > 0 && (
                <span>
                  {sup.map((c, j) => <i key={j} style={{ background: c }} />)}
                </span>
              )}
              {on && <__TwkCheck light={__twkIsLight(hero)} />}
            </button>
          );
        })}
      </div>
    </TweakRow>
  );
}

function TweakButton({ label, onClick, secondary = false }) {
  return (
    <button type="button" className={secondary ? 'twk-btn secondary' : 'twk-btn'}
            onClick={onClick}>{label}</button>
  );
}

Object.assign(window, {
  useTweaks, TweaksPanel, TweakSection, TweakRow,
  TweakSlider, TweakToggle, TweakRadio, TweakSelect,
  TweakText, TweakNumber, TweakColor, TweakButton,
});


/* ── shared.jsx ─────────────────────────────────────────── */

// Composants partagés VIDALOZZI
// (React destructuring hoisted to bundle preamble)
// ── Reveal on scroll ────────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add("in");});
    }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

// ── Nav ─────────────────────────────────────────────────────────────
function Nav({ active, lang, setLang, t, current }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.classList.toggle("nav-open", open);
    return () => document.body.classList.remove("nav-open");
  }, [open]);
  // Mobile: tapping anywhere except a nav item (link OR button — the FR/EN
  // switch uses <button>) or the burger itself closes the menu. Before, the
  // selector only whitelisted ".nav-links a", so tapping FR/EN closed the
  // menu instead of switching the language.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const el = e.target;
      if (el && el.closest && (
        el.closest(".nav-burger") ||
        el.closest(".nav-links a") ||
        el.closest(".nav-links button")
      )) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);
  const closeMenu = () => setOpen(false);
  return (
    <nav className={"nav " + (scrolled ? "nav-scrolled" : "")}>
      <a href="index.html" className="logo">
        <span className="logo-mark">V/</span>
        <span>VIDALOZZI</span>
      </a>
      <div className="nav-links">
        <a href="index.html" className={current === "home" ? "active" : ""} onClick={closeMenu}>{t.nav.home}</a>
        <a href="ugc.html" className={current === "ugc" ? "active" : ""} onClick={closeMenu}>{t.nav.ugc}</a>
        <a href="talent.html" className={current === "talent" ? "active" : ""} onClick={closeMenu}>{t.nav.talent}</a>
        <a href="blog.html" className={current === "blog" || current === "article" ? "active" : ""} onClick={closeMenu}>{t.nav.blog}</a>
        <span style={{ opacity: 0.3 }}>·</span>
        <span className="lang-switch">
          <button className={lang === "fr" ? "active" : ""} onClick={() => setLang("fr")}>FR</button>
          <span>/</span>
          <button className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>EN</button>
        </span>
      </div>
      <a href="#contact" className="nav-cta">
        <span className="nav-cta-dot" />
        {t.book}
      </a>
      <button
        className="nav-burger"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}>
        <span></span>
        <span></span>
      </button>
    </nav>);

}

// ── Marquee bandeau marques ────────────────────────────────────────
function BrandLogo({ brand }) {
  const [src, setSrc] = useState(brand.src);
  const [hover, setHover] = useState(false);
  const inputRef = React.useRef(null);

  useEffect(() => {
    let url = null;
    (async () => {
      try {
        const db = await new Promise((res, rej) => {
          const r = indexedDB.open("brand-logos", 1);
          r.onupgradeneeded = () => r.result.createObjectStore("logos");
          r.onsuccess = () => res(r.result);
          r.onerror = () => rej(r.error);
        });
        const blob = await new Promise((res, rej) => {
          const tx = db.transaction("logos", "readonly");
          const q = tx.objectStore("logos").get(brand.id);
          q.onsuccess = () => res(q.result || null);
          q.onerror = () => rej(q.error);
        });
        if (blob) { url = URL.createObjectURL(blob); setSrc(url); }
      } catch {}
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [brand.id, brand.src]);

  const onFile = async (file) => {
    if (!file) return;
    try {
      const db = await new Promise((res, rej) => {
        const r = indexedDB.open("brand-logos", 1);
        r.onupgradeneeded = () => r.result.createObjectStore("logos");
        r.onsuccess = () => res(r.result);
        r.onerror = () => rej(r.error);
      });
      await new Promise((res, rej) => {
        const tx = db.transaction("logos", "readwrite");
        tx.objectStore("logos").put(file, brand.id);
        tx.oncomplete = () => res();
        tx.onerror = () => rej(tx.error);
      });
      setSrc(URL.createObjectURL(file));
    } catch (e) { console.warn(e); }
  };

  const onReset = async (e) => {
    e.stopPropagation();
    try {
      const db = await new Promise((res, rej) => {
        const r = indexedDB.open("brand-logos", 1);
        r.onsuccess = () => res(r.result);
        r.onerror = () => rej(r.error);
      });
      await new Promise((res) => {
        const tx = db.transaction("logos", "readwrite");
        tx.objectStore("logos").delete(brand.id);
        tx.oncomplete = () => res();
      });
      setSrc(brand.src);
    } catch {}
  };

  const editable = document.documentElement.dataset.editmode === "1";
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: 56, position: "relative" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDragOver={(e) => { if (editable) e.preventDefault(); }}
      onDrop={(e) => {
        if (!editable) return;
        e.preventDefault();
        const f = e.dataTransfer?.files?.[0];
        if (f && f.type.startsWith("image/")) onFile(f);
      }}>
      <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
        <img src={src} alt={brand.name} className="brand-logo" data-brand={brand.id} />
        <span className="brand-edit-controls" style={{
          position: "absolute", inset: "-8px -10px", display: (editable && hover) ? "flex" : "none",
          alignItems: "center", justifyContent: "center", gap: 6,
          background: "rgba(14,11,8,0.75)", backdropFilter: "blur(6px)",
          border: "0.5px solid var(--line-strong)", borderRadius: 4,
          opacity: hover ? 1 : 0, transition: "opacity 0.2s",
        }}>
          <button
            onClick={() => inputRef.current?.click()}
            style={{
              appearance: "none", background: "var(--accent)", color: "var(--bg-deep)",
              border: 0, borderRadius: 999, padding: "4px 10px",
              fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
              fontFamily: "JetBrains Mono, monospace", cursor: "pointer", fontWeight: 600,
            }}>Upload</button>
          <button
            onClick={onReset}
            style={{
              appearance: "none", background: "transparent", color: "var(--ink-mute)",
              border: "0.5px solid var(--line-strong)", borderRadius: 999, padding: "4px 10px",
              fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
              fontFamily: "JetBrains Mono, monospace", cursor: "pointer",
            }}>Reset</button>
        </span>
      </span>
      <span style={{ color: "var(--accent)", fontSize: 10, opacity: 0.5 }}>✦</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        style={{ display: "none" }}
        onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
    </span>);

}

function BrandsMarquee({ label, noBorderBottom }) {
  const items = window.BRANDS;
  const Track = () =>
  <div className="marquee-track">
      {items.concat(items).map((b, i) =>
    <BrandLogo key={b.id + "-" + i} brand={b} />
    )}
    </div>;

  return (
    <section style={{ padding: "72px 0", borderTop: "0.5px solid var(--line)", borderBottom: noBorderBottom ? "0" : "0.5px solid var(--line)", background: "var(--bg-deep)" }}>
      <div className="wrap" style={{ marginBottom: 36 }}>
        <span className="eyebrow">— {label}</span>
      </div>
      <div className="marquee">
        <Track />
      </div>
    </section>);

}

// ── Témoignages ─────────────────────────────────────────────────────
function Testimonials({ t }) {
  const [filter, setFilter] = useState("ALL");
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const interacted = useRef(false);
  const touchX = useRef(null);
  const allItems = t.testimonials;
  const items = filter === "ALL" ? allItems : allItems.filter(it => it.tag === filter);
  const total = items.length;
  const safeIdx = total ? Math.min(idx, total - 1) : 0;
  const it = items[safeIdx];
  const filterLabels = t.testimonialsFilters || { all: "Tous", talent: "Talent", ugc: "UGC" };
  const avg = total ? items.reduce((s, x) => s + (x.stars || 5), 0) / total : 0;
  const avgLabel = t.testimonialsRating || { count: (n) => `${n} avis`, of: "sur 5" };
  const localeLang = t.testimonialsLocale || "fr-FR";

  useEffect(() => { setIdx(0); }, [filter]);

  // Défilement automatique : un avis toutes les 7s, en pause au survol,
  // coupé dès que le visiteur navigue lui-même ou préfère moins d'animations.
  useEffect(() => {
    if (paused || interacted.current || total < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const iv = setInterval(() => setIdx(i => (i + 1) % total), 7000);
    return () => clearInterval(iv);
  }, [paused, total, filter]);

  const goTo = (n) => { interacted.current = true; if (total) setIdx((n + total) % total); };
  const onTouchStart = (e) => { touchX.current = e.touches ? e.touches[0].clientX : null; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = (e.changedTouches ? e.changedTouches[0].clientX : touchX.current) - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 48) goTo(safeIdx + (dx < 0 ? 1 : -1));
  };

  const logoBg = it && (it.logoBg || "var(--ivory, #F4EFE6)");
  const logoScale = it && (it.logoScale || "72%");
  const AuthorWrap = it && it.url ? "a" : "div";
  const authorProps = it && it.url
    ? { href: it.url, target: "_blank", rel: "noopener noreferrer", style: { textDecoration: "none", color: "inherit" } }
    : {};

  return (
    <section>
      <div className="wrap">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>— {t.testimonialsLabel}</div>
            <h2 className="display">{t.testimonialsHead}</h2>
          </div>
          <div className="testimonials-filters" role="group" aria-label="Filtrer les avis">
            {[
              { key: "ALL", label: filterLabels.all },
              { key: "UGC", label: filterLabels.ugc },
              { key: "TALENT", label: filterLabels.talent },
            ].map(f => (
              <button
                key={f.key}
                type="button"
                className={"testimonials-filter" + (filter === f.key ? " is-active" : "")}
                onClick={() => setFilter(f.key)}
                aria-pressed={filter === f.key}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {it && (
          <div
            className="testimonial-solo"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <blockquote key={filter + "-" + safeIdx} className="testimonial-solo-quote">
              <p className="testimonial-solo-text">{it.q}</p>
              <footer className="testimonial-solo-author">
                <AuthorWrap {...authorProps} className="testimonial-solo-id">
                  <span className="ph ph-portrait testimonial-solo-avatar" style={{ background: it.logo ? logoBg : undefined }}>
                    {it.logo && <img src={it.logo} alt={it.a} style={{ width: logoScale, height: logoScale, objectFit: "contain" }} />}
                  </span>
                  <span>
                    <cite className="testimonial-solo-name">{it.a}</cite>
                    <span className="testimonial-solo-role">{it.r}</span>
                  </span>
                </AuthorWrap>
              </footer>
            </blockquote>
          </div>
        )}

        {/* data-no-edit : contenu 100% dynamique (note, compteur) — le
            content-editor ne doit pas remplacer ces nœuds texte, sinon
            React perd la main dessus et l'affichage se fige. */}
        <div className="testimonial-solo-nav" data-no-edit="true">
          <div className="testimonials-rating" aria-label={`Note moyenne ${avg.toFixed(1)} ${avgLabel.of}, ${total} avis`}>
            <span className="testimonial-stars" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, s) => (
                <span key={s} className={s < Math.round(avg) ? "is-on" : "is-off"}>★</span>
              ))}
            </span>
            <span className="mono testimonials-rating-value">
              {avg.toLocaleString(localeLang, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}<span style={{ color: "var(--ink-faint)" }}>/5</span>
            </span>
            <span className="mono testimonials-rating-count">· {avgLabel.count(total)}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="mono testimonials-counter" style={{ fontSize: 11, color: "var(--ink-faint)", fontVariantNumeric: "tabular-nums" }}>
              {String(total ? safeIdx + 1 : 0).padStart(2, "0")} / {String(Math.max(total, 1)).padStart(2, "0")}
            </span>
            <button className="nav-cta" onClick={() => goTo(safeIdx - 1)} aria-label="Avis précédent" disabled={total < 2}>←</button>
            <button className="nav-cta" onClick={() => goTo(safeIdx + 1)} aria-label="Avis suivant" disabled={total < 2}>→</button>
          </div>
        </div>
      </div>
    </section>);
}

// ── Contact ─────────────────────────────────────────────────────────
function Contact({ t }) {
  return (
    <section id="contact" style={{ background: "var(--bg-deep)", borderTop: "0.5px solid var(--line)" }}>
      <div className="wrap">
        <div className="eyebrow" style={{ marginBottom: 32 }}>— {t.contact.label}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 80, alignItems: "flex-start" }}>
          <div>
            <h2 className="display" style={{ fontSize: "clamp(64px, 9vw, 140px)", lineHeight: 0.9 }}>
              <span>{t.contact.head.l1}</span><br />
              <span className="script" style={{ color: "var(--accent)", fontSize: "1.1em", lineHeight: 0.8, display: "inline-block", transform: "translateY(0.04em) rotate(-2deg)" }}>{t.contact.head.l2}</span><br />
              <span>{t.contact.head.l3}</span>
            </h2>
          </div>
          <div style={{ paddingTop: 24 }}>
            <p style={{ color: "var(--ink-mute)", fontSize: 16, lineHeight: 1.6, marginBottom: 40, maxWidth: 420 }}>{t.contact.sub}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 48 }}>
              <a className="btn-primary" href="https://calendly.com/vidalozzi" target="_blank" rel="noreferrer">
                <span className="nav-cta-dot" style={{ background: "var(--bg-deep)" }} />
                {t.contact.ctaCalendly}
                <span style={{ marginLeft: "auto" }}>→</span>
              </a>
              <a className="btn-ghost" href="assets/cv/cv-teddy-vidal-comedien-2026.pdf" download="CV-Teddy-Vidal-Comedien-2026.pdf">
                {t.contact.ctaPressKit}
                <span style={{ marginLeft: "auto" }}>↓</span>
              </a>
            </div>
            <div style={{ borderTop: "0.5px solid var(--line)", paddingTop: 24, display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <div className="label-xs" style={{ marginBottom: 4 }}>{t.contact.emailLabel}</div>
                <a href={`mailto:${t.contact.email}`} className="display" style={{ fontSize: 22 }}>{t.contact.email}</a>
              </div>
              <div>
                <div className="label-xs" style={{ marginBottom: 4 }}>{t.contact.instaLabel}</div>
                <a href="https://instagram.com/vidalozzi" target="_blank" rel="noreferrer" className="display" style={{ fontSize: 22 }}>{t.contact.insta}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

// ── Footer ──────────────────────────────────────────────────────────
function Footer({ t }) {
  return (
    <footer className="footer">
      <div className="wrap">
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 40, marginBottom: 80 }}>
          <div>
            <div className="logo" style={{ marginBottom: 16 }}>
              <span className="logo-mark">V/</span>
              <span>VIDALOZZI</span>
            </div>
            <p style={{ color: "var(--ink-mute)", fontSize: 14, maxWidth: 340, marginBottom: 28 }}>{t.footer.tagline}</p>
            <div className="footer-social" aria-label="Réseaux sociaux">
              <a href="https://www.instagram.com/vidalozzi" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a href="https://www.linkedin.com/in/vidalozzi" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5.001 2.5 2.5 0 0 1 0-5zM3 9.75h4v11.25H3V9.75zm7 0h3.83v1.54h.05c.53-1 1.83-2.06 3.77-2.06 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.4c0-1.29-.02-2.95-1.8-2.95-1.8 0-2.08 1.4-2.08 2.85V21h-4V9.75z" />
                </svg>
              </a>
              <a href="https://www.youtube.com/@vidalozzi" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M23.5 6.2a3 3 0 0 0-2.1-2.13C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.57A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.13C4.5 20.5 12 20.5 12 20.5s7.5 0 9.4-.57a3 3 0 0 0 2.1-2.13A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z" />
                </svg>
              </a>
            </div>
          </div>
          <div>
            <div className="label-xs" style={{ marginBottom: 16 }}>{t.footer.cols.navigate}</div>
            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10, fontSize: 14 }}>
              <li><a href="index.html">{t.nav.home}</a></li>
              <li><a href="ugc.html">{t.nav.ugc}</a></li>
              <li><a href="talent.html">{t.nav.talent}</a></li>
              <li><a href="blog.html">{t.nav.blog}</a></li>
            </ul>
          </div>
          <div>
            <div className="label-xs" style={{ marginBottom: 16 }}>{t.footer.cols.services}</div>
            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10, fontSize: 14, color: "var(--ink-mute)" }}>
              <li>UGC</li>
              <li>Modeling</li>
              <li>Acting</li>
              <li>Voice-over</li>
            </ul>
          </div>
          <div>
            <div className="label-xs" style={{ marginBottom: 16 }}>{t.footer.cols.contact}</div>
            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10, fontSize: 14 }}>
              <li><a href={`mailto:${t.contact.email}`}>{t.contact.email}</a></li>
              <li><a href="https://instagram.com/vidalozzi">{t.contact.insta}</a></li>
              <li><a href="https://calendly.com/vidalozzi">Calendly →</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-display display" style={{ overflow: "hidden", whiteSpace: "nowrap" }}>VIDALOZZI<span style={{ color: "var(--accent)" }}>®</span></div>
        <div className="footer-bottom">
          <span>{t.footer.copyright}</span>
          <span className="links">
            <a href="#">Mentions légales</a>
            <a href="#">Privacy</a>
          </span>
        </div>
      </div>
    </footer>);

}

// ── Accordion FAQ ───────────────────────────────────────────────────
function Accordion({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div>
      {items.map((it, i) =>
      <div key={i} className={"accordion-item" + (open === i ? " open" : "")}>
          <button className="accordion-q" onClick={() => setOpen(open === i ? -1 : i)}>
            <span style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <span className="mono" style={{ fontSize: 11, color: "var(--accent)" }}>0{i + 1}</span>
              {it.q}
            </span>
            <span className="plus">＋</span>
          </button>
          <div className="accordion-a">
            <div>
              <div className="accordion-a-inner">{it.a}</div>
            </div>
          </div>
        </div>
      )}
    </div>);

}

// ── Mode switch UGC/Talent (sticky) ─────────────────────────────────
function ModeSwitch({ mode, setMode, current, t }) {
  // Si "current" est home/blog/article, ne pas afficher le toggle UGC↔Talent
  if (current === "home" || current === "blog" || current === "article") return null;
  return (
    <div style={{
      position: "fixed", left: "50%", transform: "translateX(-50%)",
      bottom: 24, zIndex: 90,
      display: "flex", alignItems: "center", gap: 4,
      padding: 4,
      background: "rgba(14, 11, 8, 0.78)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
      border: "0.5px solid var(--line-strong)", borderRadius: 999,
      fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase"
    }}>
      <a href="ugc.html" style={{
        padding: "10px 20px", borderRadius: 999,
        background: current === "ugc" ? "var(--accent)" : "transparent",
        color: current === "ugc" ? "var(--bg-deep)" : "var(--ink)",
        fontWeight: 500
      }}>UGC · Brands</a>
      <a href="talent.html" style={{
        padding: "10px 20px", borderRadius: 999,
        background: current === "talent" ? "var(--accent)" : "transparent",
        color: current === "talent" ? "var(--bg-deep)" : "var(--ink)",
        fontWeight: 500
      }}>Talent · Casting</a>
    </div>);

}

Object.assign(window, { useReveal, Nav, BrandsMarquee, Testimonials, Contact, Footer, Accordion, ModeSwitch });

/* ── premium-fx.jsx ─────────────────────────────────────────── */

// VIDALOZZI — Premium FX
// Trois effets premium qui élèvent le ressenti du site :
//
//   1) useLenis()         — smooth scroll inertiel global (desktop uniquement,
//                           mobile garde l'inertia native iOS qui est meilleure).
//   2) <SplitText>        — composant qui découpe son enfant texte en mots, et
//                           anime chaque mot en cascade quand il entre dans le
//                           viewport (effet "reveal éditorial").
//   3) useMagneticButtons — donne aux .btn-primary / .nav-cta un effet d'aimant
//                           qui suit le curseur. Désactivé sur tactile.
//
// Aucune dépendance npm. Lenis est chargé via CDN dans les pages HTML.

// ── 1) Lenis smooth scroll ───────────────────────────────────────────
function useLenis() {
  useEffect(() => {
    // Garde la robustesse: si Lenis n'a pas chargé, on sort silencieusement.
    if (typeof window.Lenis !== "function") return;
    // Mobile: l'inertia native d'iOS est supérieure à ce que Lenis peut faire,
    // et certains scrolls horizontaux (carousels) deviennent moins fluides
    // si on superpose Lenis. On reste sur le natif.
    if (window.matchMedia("(max-width: 860px)").matches) return;
    // Respect des préférences utilisateur (reduce motion).
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const lenis = new window.Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo out
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
    });

    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    // Ré-émet un event 'scroll' classique pour le code existant (Nav scrolled,
    // HomeHero parallax). Lenis le fait déjà via son rAF interne, donc rien
    // à patcher côté écouteurs.

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
}

// ── 2) Composant <SplitText> ─────────────────────────────────────────
// Découpe le texte en mots (pas en lettres, plus lisible) et anime chacun
// avec un délai croissant quand l'élément entre dans le viewport.
//
// Usage:
//   <h1 className="display">
//     <SplitText>{t.home.heroLine1}</SplitText>
//   </h1>
//
// Props:
//   children   : string (le texte à animer)
//   className  : forwarded sur le <span> racine
//   stagger    : délai entre chaque mot, défaut 0.06s
//   delay      : décalage de départ global, défaut 0s
function SplitText({ children, className, style, stagger = 0.06, delay = 0, as = "span" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    // reduce motion: pas d'animation, on affiche direct.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setInView(true); });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const text = typeof children === "string" ? children : String(children ?? "");
  // Split en gardant les espaces séparés pour ne pas casser le wrap naturel.
  const parts = text.split(/(\s+)/);
  const Tag = as;
  return (
    <Tag
      ref={ref}
      className={"split-text " + (inView ? "in " : "") + (className || "")}
      style={style}
      aria-label={text}
    >
      {parts.map((p, i) => {
        if (/^\s+$/.test(p)) return p;
        // index "mot uniquement" pour le calcul du delay
        const wordIndex = parts.slice(0, i).filter((q) => !/^\s+$/.test(q)).length;
        return (
          <span
            key={i}
            className="split-word"
            aria-hidden="true"
            style={{ "--split-delay": (delay + wordIndex * stagger) + "s" }}
          >
            {p}
          </span>
        );
      })}
    </Tag>
  );
}

// ── 3) Magnetic buttons ──────────────────────────────────────────────
// Effet d'aimant léger qui suit le curseur. On l'applique seulement aux
// boutons importants pour garder la rareté de l'effet.
function useMagneticButtons(selector, strength) {
  if (!selector) selector = ".btn-primary, .nav-cta";
  if (typeof strength !== "number") strength = 0.25;

  useEffect(() => {
    // Pas d'effet sur les écrans tactiles (pas de curseur).
    if (window.matchMedia("(hover: none)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const cleanups = [];
    const attach = (el) => {
      if (el.dataset.magneticAttached === "1") return;
      el.dataset.magneticAttached = "1";
      el.style.willChange = "transform";
      el.style.transition = "transform 0.25s cubic-bezier(0.2, 0.7, 0.2, 1)";
      const onMove = (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) * strength;
        const y = (e.clientY - r.top - r.height / 2) * strength;
        el.style.transform = "translate(" + x + "px, " + y + "px)";
      };
      const onLeave = () => { el.style.transform = ""; };
      el.addEventListener("mousemove", onMove);
      el.addEventListener("mouseleave", onLeave);
      cleanups.push(() => {
        el.removeEventListener("mousemove", onMove);
        el.removeEventListener("mouseleave", onLeave);
        delete el.dataset.magneticAttached;
        el.style.transform = "";
      });
    };

    // Attachement initial + ré-attachement quand le DOM change (React re-renders
    // de cartes, changement de langue, etc).
    const scan = () => document.querySelectorAll(selector).forEach(attach);
    scan();
    const mo = new MutationObserver(() => scan());
    mo.observe(document.body, { childList: true, subtree: true });
    cleanups.push(() => mo.disconnect());

    return () => cleanups.forEach((fn) => fn());
  }, [selector, strength]);
}

// ── 4) Spotlight + 3D tilt sur les cartes ────────────────────────────
// Au mousemove sur une carte ciblée, on met à jour deux CSS custom
// properties: --mx/--my (position du curseur en % dans la carte, pour
// le spotlight radial-gradient) et --rx/--ry (rotation X/Y en deg pour
// le tilt 3D). Le CSS s'occupe du rendu.
// Désactivé sur tactile et reduce-motion.
function useSpotlightTilt(selector, tiltMax) {
  if (!selector) selector = ".spotlight-tilt";
  if (typeof tiltMax !== "number") tiltMax = 6; // degrés max

  useEffect(() => {
    if (window.matchMedia("(hover: none)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const cleanups = [];
    const attach = (el) => {
      if (el.dataset.spotlightAttached === "1") return;
      el.dataset.spotlightAttached = "1";
      el.style.transformStyle = "preserve-3d";
      el.style.willChange = "transform";
      const onMove = (e) => {
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;     // 0..1
        const py = (e.clientY - r.top) / r.height;
        el.style.setProperty("--mx", (px * 100).toFixed(2) + "%");
        el.style.setProperty("--my", (py * 100).toFixed(2) + "%");
        // Tilt : on inverse Y pour que la carte penche "vers" le curseur
        const ry = (px - 0.5) * tiltMax * 2;            // -tiltMax..tiltMax
        const rx = -(py - 0.5) * tiltMax * 2;
        el.style.setProperty("--rx", rx.toFixed(2) + "deg");
        el.style.setProperty("--ry", ry.toFixed(2) + "deg");
      };
      const onLeave = () => {
        el.style.setProperty("--rx", "0deg");
        el.style.setProperty("--ry", "0deg");
        el.style.setProperty("--mx", "50%");
        el.style.setProperty("--my", "50%");
      };
      el.addEventListener("mousemove", onMove);
      el.addEventListener("mouseleave", onLeave);
      cleanups.push(() => {
        el.removeEventListener("mousemove", onMove);
        el.removeEventListener("mouseleave", onLeave);
        delete el.dataset.spotlightAttached;
      });
    };

    const scan = () => document.querySelectorAll(selector).forEach(attach);
    scan();
    const mo = new MutationObserver(() => scan());
    mo.observe(document.body, { childList: true, subtree: true });
    cleanups.push(() => mo.disconnect());

    return () => cleanups.forEach((fn) => fn());
  }, [selector, tiltMax]);
}

// ── 5) Scroll progress bar ───────────────────────────────────────────
// Barre fine de 1px en haut du viewport, accent or, qui se remplit selon
// la position de scroll. Le transform: scaleX est ce qu'il y a de plus
// performant côté GPU (pas de reflow).
function ScrollProgress() {
  const fillRef = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      if (total <= 0) {
        if (fillRef.current) fillRef.current.style.transform = "scaleX(0)";
        return;
      }
      const p = Math.max(0, Math.min(1, window.scrollY / total));
      if (fillRef.current) fillRef.current.style.transform = "scaleX(" + p + ")";
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return (
    <div
      aria-hidden="true"
      className="scroll-progress"
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0,
        height: 2,
        background: "rgba(244, 239, 230, 0.06)",
        zIndex: 2000,
        pointerEvents: "none",
      }}
    >
      <div
        ref={fillRef}
        style={{
          height: "100%",
          width: "100%",
          transformOrigin: "left center",
          transform: "scaleX(0)",
          background: "linear-gradient(90deg, var(--accent), var(--accent-glow, #E6C99B))",
          boxShadow: "0 0 12px rgba(201, 168, 124, 0.6)",
          willChange: "transform",
        }}
      />
    </div>
  );
}

// Expose au scope global pour que les autres .jsx puissent les utiliser via
// JSX (<SplitText>, <ScrollProgress>) ou via les hooks (useLenis,
// useMagneticButtons, useSpotlightTilt).
Object.assign(window, { useLenis, SplitText, useMagneticButtons, useSpotlightTilt, ScrollProgress });


/* ── home.jsx ─────────────────────────────────────────── */

// VIDALOZZI — Homepage
// (React destructuring hoisted to bundle preamble)
// Compteur animé : compte de 0 à la valeur quand le chiffre entre à l'écran.
// Accepte un suffixe non numérique ("120+" → 120 puis "+").
function CountUp({ value }) {
  const ref = React.useRef(null);
  const m = String(value).match(/^(\d+)(.*)$/);
  const target = m ? parseInt(m[1], 10) : null;
  const suffix = m ? m[2] : "";
  const [display, setDisplay] = useState(target === null ? value : 0);

  useEffect(() => {
    if (target === null) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) { setDisplay(target); return; }
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      const dur = 1400;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(Math.round(target * eased));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [target]);

  return <span ref={ref} data-no-edit="true" style={{ fontVariantNumeric: "tabular-nums" }}>{display}{suffix}</span>;
}

function HomeHero({ t }) {
  const [scrollY, setScrollY] = useState(0);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 860px)").matches);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 860px)");
    const on = () => setMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);
  // Parallax: image moves slower than scroll, content fades up.
  // Désactivé sur mobile : la barre d'URL qui se rétracte fait varier la hauteur
  // et provoque des sauts de scroll si on anime au scroll.
  const imgTranslate = mobile ? 0 : Math.min(scrollY * 0.4, 240);
  const imgScale = mobile ? 1 : 1 + Math.min(scrollY / 3000, 0.08);
  const overlayOpacity = mobile ? 0.78 : Math.min(0.55 + scrollY / 1200, 0.92);
  const contentOpacity = mobile ? 1 : Math.max(1 - scrollY / 600, 0);
  const contentTranslate = mobile ? 0 : -scrollY * 0.25;

  return (
    <>
      <section className="home-hero" style={{ position: "relative", height: "100vh", minHeight: 680, marginTop: 0, overflow: "hidden" }}>
        <div style={{
          position: "absolute", inset: 0,
          transform: `translate3d(0, ${imgTranslate}px, 0) scale(${imgScale})`,
          willChange: "transform",
          transition: "transform 0.05s linear"
        }}>
          <image-slot id="home-hero" src="assets/teddy-vidal-ugc-mannequin-comedien-paris.jpg" shape="rect" fit="cover" placeholder="Drop hero image" style={{ display: "block", width: "100%", height: "110%" }}></image-slot>
        </div>
        {/* Vignette + bottom fade so text sits well */}
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, rgba(30,29,29,0.35) 0%, rgba(30,29,29,0) 25%, rgba(30,29,29,0) 55%, rgba(30,29,29,${overlayOpacity}) 100%)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 70%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.25) 100%)", pointerEvents: "none" }} />

        {/* Floating tags */}
        <span className="tag" style={{ position: "absolute", top: 96, left: 48, background: "rgba(30,29,29,0.45)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", color: "rgba(244,239,230,0.95)", borderColor: "rgba(244,239,230,0.18)" }}>
          <span className="tag-dot" />Available
        </span>
        <span className="ph-coords" style={{ position: "absolute", top: "auto", left: "auto", bottom: 24, right: 48, color: "rgba(244,239,230,0.7)", fontSize: 10 }}>© LOUISS_PHOTOGRAPHY · 2026</span>

        {/* Centered hero text */}
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          textAlign: "center",
          opacity: contentOpacity,
          transform: `translate3d(0, ${contentTranslate}px, 0)`,
          willChange: "transform, opacity"
        }}>
          <span className="eyebrow" style={{ marginBottom: 32, color: "rgba(244,239,230,0.85)" }}>— {t.home.eyebrow}</span>
          <h1 className="display" style={{ fontSize: "clamp(80px, 13vw, 220px)", color: "var(--ink)", textShadow: "0 4px 40px rgba(0,0,0,0.45)", lineHeight: 0.88 }}>
            <SplitText>{t.home.heroLine1}</SplitText>{" "}
            <SplitText
              className="script"
              delay={0.25}
              stagger={0.08}
              style={{ color: "var(--accent-glow)", fontSize: "1.15em", lineHeight: 0.7, display: "inline-block", transform: "translateY(0.06em) rotate(-2deg)" }}
            >{t.home.heroLine2}</SplitText>
          </h1>
        </div>

        {/* Scroll cue */}
        <div style={{
          position: "absolute", left: "50%", bottom: 32, transform: "translateX(-50%)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
          color: "rgba(244,239,230,0.7)",
          fontFamily: "JetBrains Mono, monospace", fontSize: 9.5, letterSpacing: "0.2em",
          opacity: contentOpacity
        }}>
          <span>SCROLL</span>
          <span style={{ width: 1, height: 28, background: "linear-gradient(to bottom, rgba(244,239,230,0.7), transparent)" }} />
        </div>
      </section>

      <section style={{ paddingTop: 100, paddingBottom: 60 }}>
        <div className="wrap">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 64, alignItems: "flex-start" }}>
            <div className="eyebrow">— Introduction</div>
            <p style={{ fontSize: 22, lineHeight: 1.5, color: "var(--ink)", maxWidth: 640, letterSpacing: "0.005em", fontFamily: "Times" }}>
              {t.home.heroSub}
            </p>
          </div>
        </div>
      </section>
    </>);

}

function HomeSplit({ t }) {
  return (
    <section style={{ padding: "60px 0 120px" }}>
      <div className="wrap">
        <div className="eyebrow" style={{ textAlign: "center", marginBottom: 40 }}>↓ {t.home.pickPath}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {[
          { card: t.home.ugcCard, href: "ugc.html", label: "UGC" },
          { card: t.home.talentCard, href: "talent.html", label: "TALENT" }].
          map((c, i) =>
          <a key={i} href={c.href} className="lift spotlight-tilt" style={{
            position: "relative",
            display: "block",
            padding: 40,
            minHeight: 560,
            border: "0.5px solid var(--line-strong)",
            borderRadius: 4,
            background: "var(--bg-elev)",
            overflow: "hidden",
            textDecoration: "none"
          }}>
              <div style={{ position: "absolute", inset: 0, opacity: 0.7 }}>
                <image-slot id={`home-split-${c.label.toLowerCase()}`} shape="rect" fit="cover" placeholder={`Drop ${c.label} cover`} style={{ display: "block", width: "100%", height: "100%" }}></image-slot>
                <span className="ph-label" style={{ position: "absolute", top: 14, left: 14, pointerEvents: "none", zIndex: 4 }}>{c.label} · 01</span>
                <span className="ph-coords" style={{ position: "absolute", bottom: 14, right: 14, pointerEvents: "none", zIndex: 4 }}>SHOT_{String(i + 1).padStart(3, "0")} / FILM_35MM</span>
              </div>
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(14,11,8,0.4) 0%, rgba(14,11,8,0.92) 70%)" }} />
              <div style={{ position: "relative", zIndex: 2, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 480 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span className="eyebrow">— {c.card.eyebrow}</span>
                  <span className="mono" style={{ fontSize: 10, color: "var(--accent)" }}>0{i + 1} / 02</span>
                </div>
                <div>
                  <h2 className="display" style={{ fontSize: "clamp(72px, 10vw, 160px)", marginBottom: 24 }}>
                    {c.card.title}<span style={{ color: "var(--accent)" }}>.</span>
                  </h2>
                  <p style={{ color: "var(--ink-mute)", fontSize: 16, maxWidth: 380, marginBottom: 32, lineHeight: 1.5 }}>{c.card.sub}</p>
                  <div className="btn-ghost" style={{ display: "inline-flex" }}>
                    {c.card.cta}<span style={{ marginLeft: 4 }}>→</span>
                  </div>
                </div>
              </div>
            </a>
          )}
        </div>
      </div>
    </section>);

}

function HomeAbout({ t }) {
  const stats = [t.home.stat1, t.home.stat2, t.home.stat3, t.home.stat4];
  return (
    <section className="home-about" style={{ borderTop: "0.5px solid var(--line)" }}>
      <div className="wrap">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 80, alignItems: "flex-start", marginBottom: 80 }}>
          <div style={{ aspectRatio: "3/4", position: "sticky", top: 100, borderRadius: 4 }}>
            <image-slot id="home-portrait" src="assets/home-portrait.webp" shape="rect" fit="contain" placeholder="Drop portrait" style={{ display: "block", width: "100%", height: "100%" }}></image-slot>
          </div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 24 }}>— {t.home.aboutLabel}</div>
            <h2 className="display" style={{ fontSize: "clamp(48px, 5.5vw, 84px)", marginBottom: 40 }}>{t.home.aboutHead}</h2>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: "var(--ink-mute)", maxWidth: 560, marginBottom: 64 }}>{t.home.aboutBody}</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 32, paddingTop: 40, borderTop: "0.5px solid var(--line)" }}>
              {stats.map((s, i) =>
              <div key={i}>
                  <div className="display" style={{ fontSize: 56, color: "var(--accent)" }}><CountUp value={s.v} /></div>
                  <div className="label-xs" style={{ marginTop: 8 }}>{s.l}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}

function HomeShowreel({ t }) {
  const [interactive, setInteractive] = React.useState(false);
  return (
    <section style={{ padding: 0, background: "var(--bg-deep)" }}>
      <div className="wrap">
        <div
          onMouseLeave={() => setInteractive(false)}
          style={{ position: "relative", aspectRatio: "16/9", borderRadius: 4, overflow: "hidden", background: "#000", display: "block" }}>
          <iframe
            src="https://www.youtube-nocookie.com/embed/6tnX98pOnJw?autoplay=1&mute=1&loop=1&playlist=6tnX98pOnJw&controls=1&modestbranding=1&rel=0&playsinline=1"
            title="Showreel · 2026"
            loading="lazy"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, display: "block" }}>
          </iframe>
          {!interactive &&
          <div
            onClick={() => setInteractive(true)}
            aria-hidden="true"
            style={{ position: "absolute", inset: 0, zIndex: 2, cursor: "pointer", background: "transparent" }} />
          }
        </div>
      </div>
      <div className="showreel-fade" />
    </section>);

}

function HomePage({ t }) {
  useReveal();
  return (
    <>
      <HomeHero t={t} />
      <HomeSplit t={t} />
      <BrandsMarquee label={t.home.brandsLabel} noBorderBottom />
      <HomeShowreel t={t} />
      <HomeAbout t={t} />
      <Testimonials t={t} />
      <Contact t={t} />
      <Footer t={t} />
    </>);

}

window.HomePage = HomePage;

/* ── ugc.jsx ─────────────────────────────────────────── */

// VIDALOZZI — UGC Page
function UgcHero({ t }) {
  return (
    <section style={{ paddingTop: 160, paddingBottom: 80 }}>
      <div className="wrap">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 48 }}>
          <span className="eyebrow">— {t.ugc.crumb}</span>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-faint)" }}>SECTION 02 / TALENT</span>
        </div>
        <h1 className="display" style={{ fontSize: "clamp(72px, 13vw, 200px)", marginBottom: 56, lineHeight: 0.88 }}>
          <SplitText>{t.ugc.heroLine1}</SplitText><br/>
          <SplitText
            className="script"
            delay={0.2}
            stagger={0.08}
            style={{ color: "var(--accent)", fontSize: "1.1em", lineHeight: 0.8, display: "inline-block", transform: "translateY(0.05em) rotate(-2deg)" }}
          >{t.ugc.heroLine2}</SplitText>{" "}
          <SplitText delay={0.5}>{t.ugc.heroLine3}</SplitText>
        </h1>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "flex-end" }}>
          <p style={{ fontSize: 18, lineHeight: 1.55, color: "var(--ink-mute)", maxWidth: 520 }}>{t.ugc.heroSub}</p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <a className="btn-primary" href="https://calendly.com/vidalozzi" target="_blank" rel="noreferrer">
              <span className="nav-cta-dot" style={{ background: "var(--bg-deep)" }} />{t.book}<span>→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Carrousel UGC : rangée filtrable par secteur, auto-slide /3s, drag + swipe + dots ──
const UGC_GAP = 16;
// Chaque vidéo + son secteur d'activité (rubrique de réalisation)
const UGC_TILES = [
  { id: "ugc-tile-1", cat: "app" },
  { id: "ugc-tile-2", cat: "auto" },
  { id: "ugc-tile-3", cat: "food" },
  { id: "ugc-tile-4", cat: "app" },
  { id: "ugc-tile-5", cat: "food" },
  { id: "ugc-tile-6", cat: "food" },
  { id: "ugc-tile-7", cat: "app" },
  { id: "ugc-tile-8", cat: "food" },
  { id: "ugc-tile-9", cat: "auto" },   // Orok × Autobacs
  { id: "ugc-tile-10", cat: "auto" },  // Orok
];
const UGC_CATS = ["all", "auto", "app", "food"];   // ordre d'affichage des filtres
const UGC_CAT_FALLBACK = { all: "Tout", app: "Application", food: "Restaurant · Food", auto: "Auto" };

function UgcReel({ t }) {
  const [filter, setFilter] = React.useState("all");
  const row = React.useMemo(
    () => (filter === "all" ? UGC_TILES : UGC_TILES.filter((v) => v.cat === filter)).map((v) => v.id),
    [filter]
  );
  const labels = (t.ugc && t.ugc.filters) || UGC_CAT_FALLBACK;

  const len = row.length;                          // vidéos du secteur actif
  const COPIES = Math.max(2, Math.ceil(10 / len)); // assez de clones pour remplir la vue

  const [tileW, setTileW] = React.useState(280);
  const [step, setStep] = React.useState(0);
  const [anim, setAnim] = React.useState(true);
  const [dragDX, setDragDX] = React.useState(0);

  const wrapRef = React.useRef(null);
  const drag = React.useRef({ active: false, startX: 0, dx: 0, moved: false, captured: false });
  const paused = React.useRef(false);   // pause après interaction manuelle
  const hover = React.useRef(false);    // pause tant que la souris est sur le carrousel

  const tileFull = tileW + UGC_GAP;

  // Mesure responsive du tile
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      let tw;
      if (w >= 900) tw = (w - UGC_GAP * 3) / 4;        // desktop : 4 par vue
      else if (w >= 560) tw = (w - UGC_GAP) / 2;       // tablette : 2 par vue
      else tw = w * 0.78;                              // mobile : 1 + peek
      setTileW(tw);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Auto-slide toutes les 3 s
  React.useEffect(() => {
    const id = setInterval(() => {
      if (paused.current || hover.current) return;
      setAnim(true);
      setStep((s) => s + 1);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  // Change de secteur -> on revient au début, sans animer le saut
  React.useEffect(() => {
    setAnim(false);
    setStep(0);
    paused.current = false;
  }, [filter]);

  // Reset transparent en fin de transition (boucle infinie)
  const onEnd = () => {
    setStep((s) => {
      if (s >= len) { setAnim(false); return s - len; }
      if (s < 0) { setAnim(false); return s + len; }
      return s;
    });
  };

  // Détecte un appui sur un contrôle du player (boutons / volume / barre de progression)
  const isControl = (e) => {
    const path = e.composedPath ? e.composedPath() : [];
    return path.some((n) => n && n.nodeType === 1 && (
      n.tagName === "BUTTON" || n.tagName === "INPUT" ||
      (n.classList && (n.classList.contains("progress") || n.classList.contains("ctl-wrap")))
    ));
  };

  // Drag / swipe — direction-aware. On ne capture le pointer pour un swipe
  // horizontal que si le mouvement est clairement plus horizontal que
  // vertical. Sinon on "decline" et le navigateur scrolle la page
  // verticalement comme attendu. Évite que le carrousel ne fige le
  // scroll vertical quand l'utilisateur swipe en oblique pour descendre.
  const onDown = (e) => {
    if (e.button === 2) return;
    if (isControl(e)) return;                 // laisse play/pause, volume, seek au player
    drag.current = { active: true, startX: e.clientX, startY: e.clientY, dx: 0, dy: 0, moved: false, captured: false, declined: false };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d.active || d.declined) return;
    d.dx = e.clientX - d.startX;
    d.dy = e.clientY - d.startY;
    if (!d.moved) {
      const ax = Math.abs(d.dx);
      const ay = Math.abs(d.dy);
      // Pas encore assez de mouvement pour décider de la direction
      if (ax < 10 && ay < 10) return;
      // Vertical domine -> on lâche, le navigateur fait son scroll
      if (ay >= ax) {
        d.declined = true;
        return;
      }
      // Horizontal confirmé -> on capture le pointer et on démarre le drag
      d.moved = true;
      paused.current = true;
      setAnim(false);
      try { e.currentTarget.setPointerCapture(e.pointerId); d.captured = true; } catch (_) {}
    }
    if (d.moved) setDragDX(d.dx);
  };
  const onUp = () => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    if (!d.moved) return;                     // simple clic → on laisse le player gérer
    const moved = Math.round(-d.dx / tileFull);
    setDragDX(0);
    setAnim(true);
    setStep((s) => s + moved);
    setTimeout(() => { paused.current = false; }, 4000);
  };

  const dots = ((step % len) + len) % len;
  const goTo = (i) => {
    setAnim(true);
    setStep((prev) => Math.floor(prev / len) * len + i);
    paused.current = true;
    setTimeout(() => { paused.current = false; }, 5000);
  };

  const renderRow = (slots) => {
    const tiles = Array.from({ length: len * COPIES });
    const x = -(step * tileFull) + dragDX;
    return (
      <div
        style={{ overflow: "hidden", touchAction: "pan-y", cursor: drag.current.active && drag.current.moved ? "grabbing" : "grab" }}
        onMouseEnter={() => { hover.current = true; }}
        onMouseLeave={() => { hover.current = false; }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <div
          className="ugc-reel-row"
          onTransitionEnd={onEnd}
          style={{
            display: "flex",
            gap: UGC_GAP,
            transform: `translate3d(${x}px,0,0)`,
            transition: anim ? "transform 0.7s cubic-bezier(0.4,0,0.1,1)" : "none",
            willChange: "transform",
          }}
        >
          {tiles.map((_, k) => {
            const id = slots[k % len];
            return (
              <div key={`${id}-${k}`} className="ugc-reel-tile" style={{ flex: `0 0 ${tileW}px`, width: tileW }}>
                <video-slot
                  id={id}
                  src={`assets/videos/${id}.mp4`}
                  poster={`assets/videos/${id}-poster.webp`}
                  label={`UGC_${id.split("-")[2].padStart(2, "0")}`}
                  placeholder="Drop video"
                  autoplay="autoplay"
                  loop="loop"
                  muted="muted"
                  draggable="false"
                  style={{ aspectRatio: "9/16", borderRadius: 4, overflow: "hidden", display: "block" }}>
                </video-slot>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section style={{ padding: "0 0 80px" }}>
      <div className="wrap">
        <div className="ugc-filterbar" role="tablist" aria-label="Filtrer par secteur">
          {UGC_CATS.map((c) => {
            const on = filter === c;
            const n = c === "all" ? UGC_TILES.length : UGC_TILES.filter((v) => v.cat === c).length;
            return (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setFilter(c)}
                className={"ugc-filter" + (on ? " is-active" : "")}
              >
                {labels[c] || UGC_CAT_FALLBACK[c]}
                <span className="ugc-filter-count">{n}</span>
              </button>
            );
          })}
        </div>
        <div ref={wrapRef} style={{ userSelect: "none" }}>
          {renderRow(row)}
        </div>
        {/* Pavé de navigation */}
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 28 }}>
          {Array.from({ length: len }).map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Aller à la position ${i + 1}`}
              style={{
                all: "unset",
                cursor: "pointer",
                width: dots === i ? 26 : 8,
                height: 8,
                borderRadius: 999,
                background: dots === i ? "var(--accent)" : "var(--line-strong)",
                transition: "width 0.35s ease, background 0.35s ease",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function VideoTile({ src, label, coords }) {
  const videoRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(true);
  const [muted, setMuted] = React.useState(true);
  const [volume, setVolume] = React.useState(0.8);
  const [hover, setHover] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setProgress(v.currentTime / (v.duration || 1));
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, []);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    v.volume = volume;
  }, [muted, volume]);

  const togglePlay = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const onVol = (e) => {
    e.stopPropagation();
    const val = parseFloat(e.target.value);
    setVolume(val);
    setMuted(val === 0);
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    if (muted) { setMuted(false); if (volume === 0) setVolume(0.8); }
    else { setMuted(true); }
  };

  return (
    <div
      className="lift"
      style={{ aspectRatio: "9/16", position: "relative", overflow: "hidden", borderRadius: 4, background: "var(--surface)", cursor: "pointer" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={togglePlay}
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        className="hero-img"
      />
      {/* Top gradient + label */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 70, background: "linear-gradient(180deg, rgba(0,0,0,0.5), transparent)", pointerEvents: "none", opacity: hover ? 1 : 0.6, transition: "opacity 0.25s" }} />
      <span className="ph-label" style={{ position: "absolute", top: 14, left: 14, color: "rgba(244,239,230,0.92)", zIndex: 2 }}>{label}</span>
      <span className="ph-coords" style={{ position: "absolute", top: 14, right: 14, color: "rgba(244,239,230,0.8)", zIndex: 2 }}>{coords}</span>

      {/* Bottom controls overlay — fade in on hover */}
      <div style={{
        position: "absolute", left: 0, right: 0, bottom: 0,
        padding: "60px 12px 12px",
        background: "linear-gradient(0deg, rgba(0,0,0,0.7), transparent)",
        opacity: hover ? 1 : 0, transition: "opacity 0.25s ease",
        zIndex: 3, pointerEvents: hover ? "auto" : "none",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        {/* Progress bar */}
        <div style={{ width: "100%", height: 2, background: "rgba(244,239,230,0.25)", borderRadius: 2, overflow: "hidden" }}>
          <div style={{ width: `${progress * 100}%`, height: "100%", background: "var(--accent-glow, #E6C99B)", transition: "width 0.1s linear" }} />
        </div>
        {/* Controls row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} style={ctrlBtnStyle}>
            {playing ? (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="2.5" width="3.5" height="11" rx="0.5"/><rect x="9.5" y="2.5" width="3.5" height="11" rx="0.5"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5v11l9-5.5z"/></svg>
            )}
          </button>
          <button onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"} style={ctrlBtnStyle}>
            {muted || volume === 0 ? (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M7 3L4 5.5H2v5h2L7 13V3z"/><path d="M10 6l4 4M14 6l-4 4" stroke="currentColor" strokeWidth="1.2" fill="none"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M7 3L4 5.5H2v5h2L7 13V3z"/><path d="M10 5.5a3.5 3.5 0 010 5M12 4a5.5 5.5 0 010 8" stroke="currentColor" strokeWidth="1.2" fill="none"/></svg>
            )}
          </button>
          <input
            type="range"
            min="0" max="1" step="0.01"
            value={muted ? 0 : volume}
            onChange={onVol}
            onClick={(e) => e.stopPropagation()}
            aria-label="Volume"
            style={{ flex: 1, accentColor: "var(--accent-glow, #E6C99B)", height: 2 }}
          />
        </div>
      </div>
    </div>
  );
}

const ctrlBtnStyle = {
  width: 28, height: 28,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  background: "rgba(244,239,230,0.12)",
  border: "0.5px solid rgba(244,239,230,0.25)",
  borderRadius: 999,
  color: "rgba(244,239,230,0.95)",
  cursor: "pointer",
  padding: 0,
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
};

function UgcServices({ t }) {
  return (
    <section style={{ borderTop: "0.5px solid var(--line)" }}>
      <div className="wrap">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>— {t.ugc.servicesLabel}</div>
            <h2 className="display">{t.ugc.servicesHead}</h2>
          </div>
        </div>
        <div className="mobile-carousel" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0, borderTop: "0.5px solid var(--line)", borderLeft: "0.5px solid var(--line)" }}>
          {t.ugc.services.map((s, i) => (
            <div key={i} className="lift" style={{ padding: 32, borderRight: "0.5px solid var(--line)", borderBottom: "0.5px solid var(--line)", minHeight: 240, position: "relative" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
                <span className="mono" style={{ fontSize: 11, color: "var(--accent)" }}>/ {s.n}</span>
                <span style={{ color: "var(--accent)", fontSize: 12 }}>✦</span>
              </div>
              <h3 className="display" style={{ fontSize: 32, marginBottom: 16 }}>{s.t}</h3>
              <p style={{ color: "var(--ink-mute)", fontSize: 14, lineHeight: 1.6 }}>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UgcAddons({ t }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ marginTop: 40, borderTop: "0.5px solid var(--line-strong)", borderBottom: "0.5px solid var(--line-strong)" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          all: "unset",
          width: "100%",
          padding: "32px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          gap: 24,
        }}>
        <span style={{ display: "flex", alignItems: "baseline", gap: 24 }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--accent)", letterSpacing: "0.18em", textTransform: "uppercase" }}>— {t.ugc.addonsLabel}</span>
          <span className="display" style={{ fontSize: "clamp(28px, 3vw, 44px)", lineHeight: 1 }}>Options & extras</span>
        </span>
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 40, height: 40, borderRadius: 999,
          border: "0.5px solid var(--line-strong)",
          color: "var(--accent)", fontSize: 22, lineHeight: 1,
          transition: "transform 0.3s ease, background 0.2s",
          transform: open ? "rotate(45deg)" : "rotate(0deg)",
          background: open ? "rgba(201,168,124,0.08)" : "transparent",
        }}>＋</span>
      </button>
      <div style={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: "grid-template-rows 0.4s ease",
      }}>
        <div style={{ overflow: "hidden" }}>
          <div style={{ paddingBottom: 32 }}>
            {t.ugc.addons.map((a, i) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 24,
                alignItems: "baseline",
                padding: "18px 0",
                borderTop: i === 0 ? "0.5px solid var(--line)" : "0",
                borderBottom: "0.5px solid var(--line)",
              }}>
                <span style={{ fontSize: 15, color: "var(--ink)", fontFamily: "var(--font-body)" }}>{a.n}</span>
                <span className="mono" style={{
                  fontSize: 13, color: "var(--accent)",
                  fontFamily: "JetBrains Mono, monospace",
                  letterSpacing: "0.04em",
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}>{a.p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AudienceSwitch({ value, onChange, labels }) {
  const isAgency = value === "agency";
  const labelStyle = (active) => ({
    background: "transparent", border: 0, padding: 0, cursor: "pointer",
    color: active ? "var(--accent)" : "var(--ink-faint)",
    fontFamily: "JetBrains Mono, monospace", fontSize: 12,
    letterSpacing: "0.18em", textTransform: "uppercase",
    fontWeight: active ? 600 : 400,
    transition: "color 200ms ease",
  });
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 18, marginTop: -16, marginBottom: 40 }}>
      <button type="button" onClick={() => onChange("brand")} style={labelStyle(!isAgency)}>{labels.brand}</button>
      <button
        type="button"
        role="switch"
        aria-checked={isAgency}
        aria-label={`${labels.brand} / ${labels.agency}`}
        onClick={() => onChange(isAgency ? "brand" : "agency")}
        style={{
          position: "relative", width: 52, height: 28, borderRadius: 999,
          background: isAgency ? "var(--accent)" : "rgba(255,255,255,0.06)",
          border: "0.5px solid var(--line-strong)", cursor: "pointer", padding: 0,
          transition: "background 200ms ease",
        }}
      >
        <span style={{
          position: "absolute", top: 2, left: isAgency ? 25 : 2,
          width: 22, height: 22, borderRadius: 999,
          background: isAgency ? "var(--bg-deep)" : "var(--ink)",
          transition: "left 220ms cubic-bezier(0.4, 0, 0.2, 1)",
        }} />
      </button>
      <button type="button" onClick={() => onChange("agency")} style={labelStyle(isAgency)}>{labels.agency}</button>
    </div>
  );
}

function UgcPacks({ t }) {
  const [audience, setAudience] = React.useState("brand");
  const isBrand = audience === "brand";
  const packs = isBrand ? t.ugc.packs : t.ugc.packsAgency;
  const head = isBrand ? t.ugc.packsHead : t.ugc.packsHeadAgency;
  const single = packs.length === 1;
  return (
    <section style={{ background: "var(--bg-deep)", borderTop: "0.5px solid var(--line)" }}>
      <div className="wrap">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>— {t.ugc.packsLabel}</div>
            <h2 className="display">{head}</h2>
          </div>
        </div>
        <AudienceSwitch value={audience} onChange={setAudience} labels={t.ugc.audienceLabel} />
        <div className="mobile-carousel" style={{ display: "grid", gridTemplateColumns: single ? "minmax(0, 560px)" : "repeat(3, 1fr)", gap: 16, justifyContent: single ? "center" : "stretch" }}>
          {packs.map((p, i) => (            <div key={i} className={`lift spotlight-tilt pack-card${single ? " spotlight-no-tilt" : ""}`} style={{
              padding: 32,
              border: (p.featured || single) ? "0.5px solid var(--accent)" : "0.5px solid var(--line-strong)",
              borderRadius: 4,
              background: (p.featured || single) ? "linear-gradient(180deg, rgba(201,168,124,0.06), rgba(14,11,8,0))" : "transparent",
              position: "relative",
              minHeight: 480,
              display: "flex", flexDirection: "column",
            }}>
              {p.featured && (
                <span className="tag pack-featured-badge" style={{ position: "absolute", top: -12, left: 24, background: "var(--accent)", color: "var(--bg-deep)", border: "0", fontWeight: 600, whiteSpace: "nowrap" }}>
                  <span className="tag-dot" style={{ background: "var(--bg-deep)" }} />{t.ugc.featuredLabel}
                </span>
              )}
              <div className="display" style={{ fontSize: 14, color: (p.featured || single) ? "var(--accent)" : "var(--ink-faint)", textTransform: "uppercase", letterSpacing: "0.16em", fontFamily: "JetBrains Mono", marginBottom: 16 }}>
                {String(i + 1).padStart(2, "0")} · {p.name.toUpperCase()}
              </div>
              <h3 className="display" style={{ fontSize: 56, marginBottom: 8 }}>{p.name}</h3>
              <div style={{ fontSize: 18, color: "var(--accent)", marginBottom: 24, fontFamily: "Instrument Serif, serif", fontStyle: "italic" }}>{p.price}</div>
              <p style={{ color: "var(--ink-mute)", fontSize: 14, lineHeight: 1.6, marginBottom: 32 }}>{p.desc}</p>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10, fontSize: 13, marginBottom: p.footer ? 20 : 32, flex: 1 }}>
                {p.features.map((f, j) => (
                  <li key={j} style={{ display: "flex", gap: 12, alignItems: "flex-start", color: "var(--ink-mute)" }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" style={{ marginTop: 4, flexShrink: 0, color: "var(--accent)" }}>
                      <path d="M2 6.5 L4.8 9.2 L10 3.5" stroke="currentColor" strokeWidth="1.25" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ lineHeight: 1.55 }}>{f}</span>
                  </li>
                ))}
              </ul>
              {p.footer && (
                <p style={{ fontSize: 12, lineHeight: 1.55, color: "var(--ink-faint)", fontStyle: "italic", borderTop: "0.5px solid var(--line)", paddingTop: 16, marginBottom: 24 }}>
                  {p.footer}
                </p>
              )}
              <a className={(p.featured || single) ? "btn-primary" : "btn-ghost"} href="https://calendly.com/vidalozzi" style={{ alignSelf: "center" }}>
                {t.book}<span>→</span>
              </a>
            </div>
          ))}
        </div>
        {isBrand && <UgcAddons t={t} />}
      </div>
    </section>
  );
}

function UgcProcess({ t }) {
  return (
    <section>
      <div className="wrap">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>— {t.ugc.processLabel}</div>
            <h2 className="display">{t.ugc.processHead}</h2>
          </div>
        </div>
        <div className="mobile-carousel" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, borderTop: "0.5px solid var(--line)" }}>
          {t.ugc.process.map((p, i) => (            <div key={i} style={{ padding: "40px 24px 40px 0", borderRight: i < 3 ? "0.5px solid var(--line)" : "0", paddingLeft: i > 0 ? 24 : 0 }}>
              <div className="mono" style={{ fontSize: 11, color: "var(--accent)", marginBottom: 32 }}>STEP / {p.n}</div>
              <h3 className="display" style={{ fontSize: 36, marginBottom: 16 }}>{p.t}</h3>
              <p style={{ color: "var(--ink-mute)", fontSize: 14, lineHeight: 1.6 }}>{p.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UgcFaq({ t }) {
  return (
    <section style={{ borderTop: "0.5px solid var(--line)" }}>
      <div className="wrap">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 64, alignItems: "flex-start" }}>
          <div style={{ position: "sticky", top: 100 }}>
            <div className="eyebrow" style={{ marginBottom: 16 }}>— FAQ</div>
            <h2 className="display" style={{ fontSize: "clamp(48px, 5vw, 80px)" }}>{t.ugc.faqHead}</h2>
          </div>
          <Accordion items={t.ugc.faq} />
        </div>
      </div>
    </section>
  );
}

function UgcPage({ t }) {
  useReveal();
  return (
    <>
      <UgcHero t={t} />
      <UgcReel t={t} />
      <UgcServices t={t} />
      <UgcPacks t={t} />
      <BrandsMarquee label={t.home.brandsLabel} />
      <UgcProcess t={t} />
      <UgcFaq t={t} />
      <Testimonials t={t} />
      <Contact t={t} />
      <Footer t={t} />
    </>
  );
}

window.UgcPage = UgcPage;


/* ── talent.jsx ─────────────────────────────────────────── */

// VIDALOZZI — Talent Page

function TalentHero({ t }) {
  return (
    <section style={{ paddingTop: 160, paddingBottom: 80 }}>
      <div className="wrap">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 48 }}>
          <span className="eyebrow">— {t.talent.crumb}</span>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-faint)" }}>SECTION 01 / UGC</span>
        </div>
        <h1 className="display" style={{ fontSize: "clamp(80px, 14vw, 220px)", marginBottom: 56, lineHeight: 0.88 }}>
          <SplitText>{t.talent.heroLine1}</SplitText><br />
          <SplitText
            className="script"
            delay={0.25}
            stagger={0.08}
            style={{ color: "var(--accent)", fontSize: "1.1em", lineHeight: 0.8, display: "inline-block", transform: "translateY(0.05em) rotate(-2deg)" }}
          >{t.talent.heroLine2}</SplitText>
        </h1>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "flex-end" }}>
          <p style={{ fontSize: 18, lineHeight: 1.55, color: "var(--ink-mute)", maxWidth: 520 }}>{t.talent.heroSub}</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <a className="btn-ghost" href="assets/cv/cv-teddy-vidal-comedien-2026.pdf" download="CV-Teddy-Vidal-Comedien-2026.pdf" style={{ display: "inline-flex" }}>↓ {t.talent.pressKit}</a>
          </div>
        </div>
      </div>
    </section>);

}

function TalentPortfolio({ t }) {
  // Editorial & Campagne — mixed-orientation grid, persisted server-side via <image-slot>
  const gridRef = React.useRef(null);

  // Detect landscape vs portrait per slot once filled, tag for CSS span control.
  React.useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const slots = Array.from(grid.querySelectorAll("image-slot"));

    const tag = (slot) => {
      const img = slot.shadowRoot && slot.shadowRoot.querySelector("img");
      if (!img) return;
      const apply = () => {
        if (img.naturalWidth && img.naturalHeight) {
          const isLandscape = img.naturalWidth > img.naturalHeight * 1.05;
          slot.classList.toggle("is-landscape", isLandscape);
          slot.classList.toggle("is-portrait", !isLandscape);
        }
      };
      if (img.complete) apply();
      img.addEventListener("load", apply);
      return () => img.removeEventListener("load", apply);
    };

    const cleanups = [];
    slots.forEach((slot) => {
      // Poll briefly while shadowDOM hydrates, then settle.
      let attempts = 0;
      const tick = () => {
        if (slot.shadowRoot && slot.shadowRoot.querySelector("img")) {
          const c = tag(slot);
          if (c) cleanups.push(c);
        } else if (attempts++ < 30) {
          setTimeout(tick, 100);
        }
      };
      tick();
      // Re-tag when slot becomes filled (image swap).
      const obs = new MutationObserver(() => {
        const c = tag(slot);
        if (c) cleanups.push(c);
      });
      obs.observe(slot, { attributes: true, attributeFilter: ["data-filled"] });
      cleanups.push(() => obs.disconnect());
    });
    return () => cleanups.forEach((fn) => fn && fn());
  }, []);

  return (
    <section style={{ paddingTop: 0 }}>
      <div className="wrap">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>— {t.talent.portfolioLabel}</div>
            <h2 className="display">{t.talent.portfolioHead}</h2>
          </div>
          <div className="mono" style={{ fontSize: 11, color: "var(--ink-faint)" }}>2023 — 2026</div>
        </div>
        <div className="talent-portfolio-grid" ref={gridRef}>
          {Array.from({ length: 9 }).map((_, i) =>
            <image-slot
              key={i}
              id={`portfolio-${i + 1}`}
              src={`assets/portfolio-${i + 1}.webp`}
              shape="rounded"
              radius="4"
              fit="cover"
              placeholder="Editorial / Campagne"
              style={{ display: "block", width: "100%" }}>
            </image-slot>
          )}
        </div>
        <PortfolioMobileCarousel />
      </div>
    </section>);

}

// Mobile-only 3D carousel for Portfolio. Auto-advances, swipe + tap to
// navigate, tap center to open the shared lightbox (with arrow nav between
// all photos). Hidden on desktop and in edit mode (so the grid stays usable
// for managing slots).
function PortfolioMobileCarousel() {
  const [images, setImages] = React.useState([]);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    // Photos are baked into real files (assets/portfolio-N.webp) so the
    // carousel works online without depending on the state sidecar.
    const arr = [];
    for (let i = 1; i <= 9; i++) arr.push({ src: `assets/portfolio-${i}.webp`, alt: `Editorial ${i}` });
    setImages(arr);
    setCurrentIndex(Math.floor(arr.length / 2));
  }, []);

  const next = React.useCallback(() => {
    setCurrentIndex((i) => images.length ? (i + 1) % images.length : 0);
  }, [images.length]);
  const prev = React.useCallback(() => {
    setCurrentIndex((i) => images.length ? (i - 1 + images.length) % images.length : 0);
  }, [images.length]);

  // Auto-advance — pause briefly after user interaction
  React.useEffect(() => {
    if (paused || images.length <= 1) return;
    const t = setInterval(next, 4500);
    return () => clearInterval(t);
  }, [paused, images.length, next]);

  const touchRef = React.useRef({ x: 0, y: 0, t: 0 });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2 && dt < 700) {
      dx < 0 ? next() : prev();
    }
    // Resume auto-play after a short pause so user reading isn't interrupted
    setTimeout(() => setPaused(false), 3500);
  };

  const openLightbox = () => {
    document.dispatchEvent(new CustomEvent("vz-open-gallery", {
      detail: {
        items: images.map((i) => i.src),
        index: currentIndex
      }
    }));
  };

  if (!images.length) return null;

  return (
    <div
      className="portfolio-mobile-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}>

      <div
        className="pmc-stage"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}>

        {images.map((img, i) => {
          const total = images.length;
          let pos = (i - currentIndex + total) % total;
          if (pos > Math.floor(total / 2)) pos = pos - total;
          const isCenter = pos === 0;
          const isAdjacent = Math.abs(pos) === 1;
          const visible = Math.abs(pos) <= 1;
          return (
            <div
              key={i}
              className={"pmc-card" + (isCenter ? " is-center" : "")}
              style={{
                transform:
                "translateX(" + pos * 58 + "%) " +
                "scale(" + (isCenter ? 1 : 0.78) + ") " +
                "rotateY(" + pos * -10 + "deg)",
                zIndex: isCenter ? 10 : isAdjacent ? 5 : 1,
                opacity: isCenter ? 1 : isAdjacent ? 0.45 : 0,
                visibility: visible ? "visible" : "hidden",
                pointerEvents: isCenter || isAdjacent ? "auto" : "none"
              }}
              onClick={() => {
                if (isCenter) openLightbox();
                else if (pos < 0) prev();
                else next();
              }}>

              <img src={img.src} alt={img.alt} draggable="false" />
            </div>);

        })}
      </div>
      <div className="pmc-controls" data-no-edit>
        <button className="pmc-btn" onClick={prev} aria-label="Précédent">←</button>
        <span className="pmc-counter mono">
          {String(currentIndex + 1).padStart(2, "0")} <span style={{ opacity: 0.4 }}>/ {String(images.length).padStart(2, "0")}</span>
        </span>
        <button className="pmc-btn" onClick={next} aria-label="Suivant">→</button>
      </div>
    </div>);

}

function TalentFilmo({ t }) {
  // Hover: vidéo BTS 9:16 qui suit le curseur (loop muet, autoplay).
  // Clic: lightbox plein écran avec le projet final (avec son).
  // Mobile / edit mode: tout désactivé pour ne pas gêner.
  const [hover, setHover] = React.useState(null);  // { src, x, y } | null
  const [active, setActive] = React.useState(-1);
  const [open, setOpen] = React.useState(null);    // { src, title } | null
  const previewVideoRef = React.useRef(null);

  const isInteractive = () => {
    if (typeof window === "undefined") return false;
    if (window.matchMedia("(hover: none)").matches) return false;
    if (document.documentElement.dataset.editmode === "1") return false;
    return true;
  };

  // Convention de nommage des assets (pas de copy.js change requis):
  //   assets/filmo-<i>-hover.{webp,jpg,png,mp4}  -> image OU vidéo pour le hover
  //   assets/filmo-<i>-final.mp4                  -> vidéo finie pour la lightbox
  // Si un fichier manque, l'interaction correspondante est ignorée.
  // L'ordre de priorité pour le hover : webp > jpg > png > mp4.
  const HOVER_EXTS = ["webp", "jpg", "png", "mp4"];
  const finalSrc = (i) => "assets/filmo-" + i + "-final.mp4";

  // On vérifie en HEAD au mount quels assets existent par ligne.
  // availability[i] = { hover: {src, kind: "image"|"video"} | null, final: bool }
  const [availability, setAvailability] = React.useState({});
  React.useEffect(() => {
    let cancelled = false;
    const tryHover = async (i) => {
      for (const ext of HOVER_EXTS) {
        const src = "assets/filmo-" + i + "-hover." + ext;
        const ok = await fetch(src, { method: "HEAD" }).then((r) => r.ok).catch(() => false);
        if (ok) return { src, kind: ext === "mp4" ? "video" : "image" };
      }
      return null;
    };
    const check = async () => {
      const avail = {};
      for (let i = 0; i < t.talent.films.length; i++) {
        const [h, fin] = await Promise.all([
          tryHover(i),
          fetch(finalSrc(i), { method: "HEAD" }).then((r) => r.ok).catch(() => false),
        ]);
        avail[i] = { hover: h, final: fin };
      }
      if (!cancelled) setAvailability(avail);
    };
    check();
    return () => { cancelled = true; };
  }, [t.talent.films.length]);

  const onRowEnter = (i, e) => {
    if (!isInteractive()) return;
    setActive(i);
    const a = availability[i];
    if (!a || !a.hover) return;
    setHover({ src: a.hover.src, kind: a.hover.kind, x: e.clientX, y: e.clientY });
  };
  const onRowMove = (e) => {
    if (!isInteractive()) return;
    setHover((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : null));
  };
  const onRowLeave = () => {
    setActive(-1);
    setHover(null);
  };
  const onRowClick = (i, f) => {
    if (window.matchMedia("(max-width: 860px)").matches) return; // mobile: pas d'interaction
    const a = availability[i];
    if (!a || !a.final) return;
    setOpen({ src: finalSrc(i), title: f.t });
  };

  const close = () => setOpen(null);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <section style={{ borderTop: "0.5px solid var(--line)" }}>
      <div className="wrap">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>— {t.talent.filmoLabel}</div>
            <h2 className="display">{t.talent.filmoHead}</h2>
          </div>
        </div>
        <div className="filmo-table" style={{ borderTop: "0.5px solid var(--line)" }}>
          <div className="filmo-row filmo-head" style={{ display: "grid", gridTemplateColumns: "80px 2fr 1.4fr 2fr 1fr 60px", padding: "16px 0", borderBottom: "0.5px solid var(--line)", color: "var(--ink-faint)", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: "JetBrains Mono" }}>
            <span>ANNÉE</span><span>Title</span><span>Role</span><span>Direction · Studio</span><span>Type</span><span></span>
          </div>
          {t.talent.films.map((f, i) => {
            const avail = availability[i] || { hover: false, final: false };
            return (
              <div
                key={i}
                className={"lift filmo-row" + (active === i ? " is-active" : "") + (avail.final ? " has-final" : "")}
                style={{
                  display: "grid",
                  gridTemplateColumns: "80px 2fr 1.4fr 2fr 1fr 60px",
                  padding: "28px 0",
                  borderBottom: "0.5px solid var(--line)",
                  alignItems: "center",
                  cursor: avail.final ? "pointer" : "default"
                }}
                onMouseEnter={(e) => onRowEnter(i, e)}
                onMouseMove={onRowMove}
                onMouseLeave={onRowLeave}
                onClick={() => onRowClick(i, f)}
              >
                <span className="filmo-year mono" style={{ fontSize: 12, color: "var(--accent)" }}>{f.y}</span>
                <span className="filmo-title display" style={{ fontSize: 28 }}>{f.t}</span>
                <span className="filmo-role" style={{ fontSize: 14, color: "var(--ink-mute)" }}>{f.role}</span>
                <span className="filmo-dir" style={{ fontSize: 13, color: "var(--ink-mute)" }}>{f.dir}</span>
                <span className="filmo-type tag" style={{ alignSelf: "center", height: "28.5px" }}><span className="tag-dot" />{f.type}</span>
                {/* Indicateur de disponibilité d'une vidéo : un petit cercle
                    or quand un final est dispo. Discret mais lisible. */}
                <span className="filmo-end-marker" style={{ textAlign: "right" }}>
                  {avail.final && (
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontFamily: "JetBrains Mono, monospace",
                      fontSize: 10,
                      letterSpacing: "0.14em",
                      color: "var(--accent)",
                      textTransform: "uppercase",
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: 999, background: "var(--accent)", boxShadow: "0 0 8px var(--accent)" }} />
                      Play
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview vidéo 9:16 flottante qui suit le curseur */}
      {hover && (
        <div
          className="filmo-hover-preview"
          style={{
            position: "fixed",
            top: hover.y,
            left: hover.x,
            transform: "translate(24px, -50%)",
            pointerEvents: "none",
            zIndex: 1500,
            width: 160,
            aspectRatio: "9 / 16",
            borderRadius: 6,
            overflow: "hidden",
            border: "0.5px solid var(--line-strong)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.45), 0 0 0 0.5px rgba(201,168,124,0.25)",
            background: "var(--bg-deep)",
          }}
        >
          {hover.kind === "video" ? (
            <video
              ref={previewVideoRef}
              key={hover.src}
              src={hover.src}
              autoPlay
              muted
              loop
              playsInline
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <img
              key={hover.src}
              src={hover.src}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          )}
        </div>
      )}

      {/* Lightbox vidéo finale (clic) */}
      {open && (
        <div
          onClick={close}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(10,8,6,0.94)",
            backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
            cursor: "zoom-out",
            animation: "lb-fade 0.25s ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              width: "min(1200px, 92vw)",
              aspectRatio: "16 / 9",
              borderRadius: 6,
              overflow: "hidden",
              boxShadow: "0 40px 80px rgba(0,0,0,0.6)",
              background: "#000",
            }}
          >
            <video
              key={open.src}
              src={open.src}
              controls
              autoPlay
              playsInline
              style={{ width: "100%", height: "100%", display: "block", background: "#000" }}
            />
          </div>
          <div style={{
            position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)",
            fontFamily: "JetBrains Mono", fontSize: 10, letterSpacing: "0.16em",
            color: "rgba(244,239,230,0.5)", textTransform: "uppercase",
            pointerEvents: "none",
          }}>
            {open.title} · ESC pour fermer
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            aria-label="Close"
            style={{
              position: "absolute", top: 24, right: 24,
              width: 44, height: 44, borderRadius: 999,
              background: "rgba(244,239,230,0.1)",
              border: "0.5px solid rgba(244,239,230,0.3)",
              color: "var(--ink)",
              fontFamily: "JetBrains Mono", fontSize: 14,
              cursor: "pointer",
              backdropFilter: "blur(8px)",
            }}
          >✕</button>
        </div>
      )}
    </section>);

}

function TalentStats({ t }) {
  const s = t.talent.stats;
  const rows = [
  { l: "Height", v: s.height },
  { l: "Chest", v: s.chest },
  { l: "Waist", v: s.waist },
  { l: "Suit", v: s.suit },
  { l: "Shoe", v: s.shoe },
  { l: "Hair", v: s.hair },
  { l: "Eyes", v: s.eyes }];

  return (
    <section style={{ background: "var(--bg-deep)", borderTop: "0.5px solid var(--line)" }}>
      <div className="wrap">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>— {t.talent.statsLabel}</div>
            <h2 className="display">{t.talent.statsHead}</h2>
          </div>
          <a className="btn-ghost" href="assets/cv/cv-teddy-vidal-comedien-2026.pdf" download="CV-Teddy-Vidal-Comedien-2026.pdf" id="press">↓ {t.talent.pressKit}</a>
        </div>
        <div className="talent-stats-grid">
          <div className="talent-polaroids-grid">
            {["polaroid-01", "polaroid-02", "polaroid-03", "polaroid-04"].map((id) =>
              <image-slot
                key={id}
                id={id}
                src={`assets/${id}.webp`}
                shape="rounded"
                radius="4"
                fit="contain"
                placeholder="Pola"
                style={{ display: "block", width: "100%", aspectRatio: "4 / 5" }}>
              </image-slot>
            )}
          </div>
          <div className="talent-cv">
            <div style={{ borderTop: "0.5px solid var(--line)" }}>
              {rows.map((r, i) =>
              <div key={i} className="cv-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "20px 0", borderBottom: "0.5px solid var(--line)", gap: 16 }}>
                  <span className="label-xs">{r.l}</span>
                  <span className="display cv-val" style={{ fontSize: 22 }}>{r.v}</span>
                </div>
              )}
            </div>
            <div className="cv-extras" style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 24 }}>
              <div>
                <div className="label-xs" style={{ marginBottom: 8 }}>Languages</div>
                <p style={{ fontSize: 16, color: "var(--ink-mute)" }}>{s.languages}</p>
              </div>
              <div>
                <div className="label-xs" style={{ marginBottom: 8 }}>Skills</div>
                <p style={{ fontSize: 16, color: "var(--ink-mute)" }}>{s.skills}</p>
              </div>
              <div>
                <div className="label-xs" style={{ marginBottom: 8 }}>Location</div>
                <p style={{ fontSize: 16, color: "var(--ink-mute)" }}>{s.location}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

function TalentAgencies({ t }) {
  return (
    <section>
      <div className="wrap">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>— {t.talent.agenciesLabel}</div>
            <h2 className="display">{t.talent.agenciesHead}</h2>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {t.talent.agencies.map((a, i) =>
          <div key={i} className="lift" style={{
            padding: 40, border: "0.5px solid var(--line-strong)", borderRadius: 4,
            minHeight: 220, display: "flex", flexDirection: "column", justifyContent: "space-between",
            background: "var(--bg-elev)"
          }}>
              <div className="mono" style={{ fontSize: 11, color: "var(--accent)" }}>0{i + 1} / 02</div>
              <div>
                <h3 className="display" style={{ fontSize: 48, marginBottom: 12 }}>{a.name}</h3>
                <p style={{ color: "var(--ink-mute)", fontSize: 14, marginBottom: 24 }}>{a.scope}</p>
                <a href={`mailto:${a.contact}`} style={{ display: "inline-flex", gap: 12, alignItems: "center", color: "var(--accent)", fontSize: 14, fontFamily: "JetBrains Mono", letterSpacing: "0.06em" }}>
                  → {a.contact}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}

function ImageLightbox() {
  const [gallery, setGallery] = React.useState(null); // { items: string[], index: number }

  React.useEffect(() => {
    const onClick = (e) => {
      const path = e.composedPath();
      if (path.some((n) => n.tagName === "BUTTON")) return;
      if (document.documentElement.dataset.editmode === "1") return;
      const slot = path.find((n) => n.tagName && n.tagName.toLowerCase() === "image-slot");
      if (!slot) return;
      if (!slot.hasAttribute("data-filled")) return;
      const img = slot.shadowRoot && slot.shadowRoot.querySelector("img");
      if (!img || !img.src) return;
      e.preventDefault();
      // Group: all filled image-slots inside the same grid container as the clicked slot
      // (Portfolio grid, polaroids grid, etc). Fall back to standalone if none.
      const container =
      slot.closest(".talent-portfolio-grid, .talent-polaroids-grid") || slot.parentElement;
      const siblings = Array.from(container.querySelectorAll("image-slot")).
      filter((s) => s.hasAttribute("data-filled")).
      map((s) => {
        const i = s.shadowRoot && s.shadowRoot.querySelector("img");
        return i && i.src;
      }).
      filter(Boolean);
      const idx = Math.max(0, siblings.indexOf(img.src));
      setGallery({ items: siblings.length ? siblings : [img.src], index: idx });
    };
    document.addEventListener("click", onClick);
    // Also accept a programmatic open (mobile carousel, etc.)
    const onOpen = (e) => {
      const d = e.detail || {};
      if (Array.isArray(d.items) && d.items.length) {
        setGallery({ items: d.items, index: Math.max(0, Math.min(d.index || 0, d.items.length - 1)) });
      }
    };
    document.addEventListener("vz-open-gallery", onOpen);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("vz-open-gallery", onOpen);
    };
  }, []);

  const close = React.useCallback(() => setGallery(null), []);
  const go = React.useCallback((dir) => {
    setGallery((g) => {
      if (!g) return g;
      const n = g.items.length;
      return { items: g.items, index: (g.index + dir + n) % n };
    });
  }, []);

  React.useEffect(() => {
    if (!gallery) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [gallery, close, go]);

  // Touch swipe (mobile)
  const touchRef = React.useRef({ x: 0, y: 0, t: 0 });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.4 && dt < 600) {
      go(dx < 0 ? 1 : -1);
    }
  };

  if (!gallery) return null;
  const src = gallery.items[gallery.index];
  const multi = gallery.items.length > 1;
  return (
    <div
      onClick={close}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: "fixed", inset: 0, zIndex: 9998,
        background: "rgba(10,8,6,0.94)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, cursor: "zoom-out",
        animation: "lb-fade 0.25s ease",
        touchAction: "pan-y"
      }}>

      <style>{`
        @keyframes lb-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes lb-img-in { from { opacity: 0; transform: scale(0.985) } to { opacity: 1; transform: scale(1) } }
        .lb-nav { position: absolute; top: 50%; transform: translateY(-50%);
          width: 56px; height: 56px; border-radius: 999px;
          background: rgba(244,239,230,0.08);
          border: 0.5px solid rgba(244,239,230,0.3);
          color: var(--ink, #F4EFE6);
          font-family: 'JetBrains Mono', monospace; font-size: 20px;
          cursor: pointer; backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          transition: background 0.2s, transform 0.2s;
        }
        .lb-nav:hover { background: rgba(244,239,230,0.18); transform: translateY(-50%) scale(1.05); }
        .lb-nav.prev { left: 24px; }
        .lb-nav.next { right: 24px; }
        @media (max-width: 720px) {
          .lb-nav { display: none !important; }
        }
      `}</style>

      <img
        key={src}
        src={src}
        alt=""
        style={{
          maxWidth: "100%", maxHeight: "100%",
          objectFit: "contain",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
          borderRadius: 2,
          animation: "lb-img-in 0.28s ease"
        }}
        onClick={(e) => e.stopPropagation()} />


      {multi &&
      <>
          <button
          className="lb-nav prev"
          aria-label="Previous"
          onClick={(e) => {e.stopPropagation();go(-1);}}>
          ←
        </button>
          <button
          className="lb-nav next"
          aria-label="Next"
          onClick={(e) => {e.stopPropagation();go(1);}}>
          →
        </button>
        </>
      }

      <button
        onClick={(e) => {e.stopPropagation();close();}}
        aria-label="Close"
        style={{
          position: "absolute", top: 24, right: 24,
          width: 44, height: 44, borderRadius: 999,
          background: "rgba(244,239,230,0.1)",
          border: "0.5px solid rgba(244,239,230,0.3)",
          color: "var(--ink, #F4EFE6)",
          fontFamily: "JetBrains Mono", fontSize: 14,
          cursor: "pointer",
          backdropFilter: "blur(8px)"
        }}>
        ✕
      </button>

      <div style={{
        position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)",
        fontFamily: "JetBrains Mono", fontSize: 10, letterSpacing: "0.16em",
        color: "rgba(244,239,230,0.5)", textTransform: "uppercase",
        pointerEvents: "none",
        whiteSpace: "nowrap",
        display: "flex", gap: 16, alignItems: "center"
      }}>
        {multi &&
        <span>{String(gallery.index + 1).padStart(2, "0")} / {String(gallery.items.length).padStart(2, "0")}</span>
        }
        <span>{multi ? "← → · Swipe · ESC" : "Tap outside · ESC"}</span>
      </div>
    </div>);

}

function TalentPage({ t }) {
  useReveal();
  return (
    <>
      <TalentHero t={t} />
      <TalentPortfolio t={t} />
      <TalentFilmo t={t} />
      <TalentStats t={t} />
      <TalentAgencies t={t} />
      <BrandsMarquee label={t.home.brandsLabel} />
      <Testimonials t={t} />
      <Contact t={t} />
      <Footer t={t} />
      <ImageLightbox />
    </>);

}

window.TalentPage = TalentPage;


/* ── blog.jsx ─────────────────────────────────────────── */

// VIDALOZZI — Blog (liste + lecteur d'article)
// (React destructuring hoisted to bundle preamble)
const BLOG_UI = {
  fr: {
    eyebrow: "Journal",
    headLine1: "Le",
    headScript: "journal",
    headDot: ".",
    sub: "Notes sur l'UGC, la performance et le métier de créateur. Stratégie, tarifs, méthode — ce que j'aurais aimé lire avant de me lancer.",
    all: "Tout",
    featured: "À la une",
    more: "Articles",
    read: "Lire l'article",
    minRead: "min de lecture",
    back: "← Le journal",
    related: "À lire ensuite",
    ctaHead: "Un projet UGC en tête ?",
    ctaSub: "Parlons-en. Un appel de quinze minutes suffit pour cadrer deux ou trois angles à tester.",
    ctaBtn: "Réserver un appel",
    by: "Par Teddy Vidal",
    notFound: "Article introuvable.",
    notFoundLink: "Retour au journal",
  },
  en: {
    eyebrow: "Journal",
    headLine1: "The",
    headScript: "journal",
    headDot: ".",
    sub: "Notes on UGC, performance and the creator craft. Strategy, pricing, method — what I wish I'd read before starting.",
    all: "All",
    featured: "Featured",
    more: "Articles",
    read: "Read article",
    minRead: "min read",
    back: "← The journal",
    related: "Read next",
    ctaHead: "Got a UGC project in mind?",
    ctaSub: "Let's talk. A fifteen-minute call is enough to frame two or three angles to test.",
    ctaBtn: "Book a call",
    by: "By Teddy Vidal",
    notFound: "Article not found.",
    notFoundLink: "Back to the journal",
  },
};

function getPosts() { return window.BLOG_POSTS || []; }
function getPost(slug) { return getPosts().find((p) => p.slug === slug); }

// ── Publication par date ────────────────────────────────────────────
// Un article devient visible le jour de sa dateISO. Avant, il est masqué
// de la liste, des "articles liés" et passe en noindex. Aucune action requise.
function todayMidnight() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
function isPublished(p) { return new Date(p.dateISO + "T00:00:00") <= todayMidnight(); }
function publishedPosts() {
  return getPosts().filter(isPublished).sort((a, b) => b.dateISO.localeCompare(a.dateISO));
}
function fmtDate(iso, lang) {
  const d = new Date(iso + "T00:00:00");
  const s = new Intl.DateTimeFormat(lang === "en" ? "en-US" : "fr-FR", { month: "long", year: "numeric" }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function setRobots(index) {
  let m = document.querySelector('meta[name="robots"]');
  if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
  m.setAttribute("content", index ? "index, follow, max-image-preview:large, max-snippet:-1" : "noindex, nofollow");
}

// ── Carte article ───────────────────────────────────────────────────
function PostCard({ post, lang, ui, large }) {
  const c = post[lang];
  const cat = post.category[lang];
  return (
    <a href={post.slug + ".html"} className="blog-card lift" data-large={large ? "1" : undefined}>
      <div className={"ph blog-thumb" + (large ? " blog-thumb-lg" : "")}>
        <img className="blog-cover-img" src={"assets/blog/" + post.slug + ".jpg"} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        <span className="ph-label">{post.hero}</span>
        <span className="ph-coords">VIDALOZZI · JOURNAL</span>
      </div>
      <div className="blog-card-body">
        <div className="blog-meta">
          <span className="tag"><span className="tag-dot" />{cat}</span>
          <span className="mono blog-meta-time">{fmtDate(post.dateISO, lang)} · {post.readMin} {ui.minRead}</span>
        </div>
        <h3 className="display blog-card-title">{c.title}</h3>
        <p className="blog-dek">{c.dek}</p>
        <span className="blog-readlink">{ui.read} <span aria-hidden="true">→</span></span>
      </div>
    </a>
  );
}

// ── Page liste ──────────────────────────────────────────────────────
function BlogPage({ t, lang }) {
  useReveal();
  const ui = BLOG_UI[lang] || BLOG_UI.fr;
  const posts = publishedPosts();
  const [filter, setFilter] = useState("__all");

  const categories = useMemo(() => {
    const seen = [];
    posts.forEach((p) => { const c = p.category[lang]; if (!seen.includes(c)) seen.push(c); });
    return seen;
  }, [lang, posts]);

  const featured = posts[0];
  const rest = posts.slice(1);
  const shown = filter === "__all" ? rest : rest.filter((p) => p.category[lang] === filter);
  const showFeatured = filter === "__all" || (featured && featured.category[lang] === filter);

  return (
    <>
      <section className="blog-hero">
        <div className="wrap">
          <div className="eyebrow reveal" style={{ marginBottom: 24 }}>— {ui.eyebrow}</div>
          <h1 className="display reveal blog-hero-title">
            {ui.headLine1}{" "}
            <span className="script" style={{ color: "var(--accent)", fontSize: "1.05em", display: "inline-block", transform: "translateY(0.04em) rotate(-2deg)" }}>{ui.headScript}</span>{ui.headDot}
          </h1>
          <p className="blog-hero-sub reveal">{ui.sub}</p>
          <div className="blog-filters reveal">
            <button className={"blog-filter" + (filter === "__all" ? " is-active" : "")} onClick={() => setFilter("__all")}>{ui.all}</button>
            {categories.map((c) => (
              <button key={c} className={"blog-filter" + (filter === c ? " is-active" : "")} onClick={() => setFilter(c)}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      {showFeatured && featured && (
        <section className="blog-featured-sec">
          <div className="wrap">
            <div className="eyebrow reveal" style={{ marginBottom: 28 }}>— {ui.featured}</div>
            <a href={featured.slug + ".html"} className="blog-featured lift reveal">
              <div className="ph blog-featured-img">
                <img className="blog-cover-img" src={"assets/blog/" + featured.slug + ".jpg"} alt="" loading="lazy" />
                <span className="ph-label">{featured.hero}</span>
                <span className="ph-coords">VIDALOZZI · JOURNAL</span>
              </div>
              <div className="blog-featured-body">
                <div className="blog-meta">
                  <span className="tag"><span className="tag-dot" />{featured.category[lang]}</span>
                  <span className="mono blog-meta-time">{fmtDate(featured.dateISO, lang)} · {featured.readMin} {ui.minRead}</span>
                </div>
                <h2 className="display blog-featured-title">{featured[lang].title}</h2>
                <p className="blog-dek blog-dek-lg">{featured[lang].dek}</p>
                <span className="blog-readlink">{ui.read} <span aria-hidden="true">→</span></span>
              </div>
            </a>
          </div>
        </section>
      )}

      <section className="blog-grid-sec">
        <div className="wrap">
          {filter === "__all" && <div className="eyebrow reveal" style={{ marginBottom: 28 }}>— {ui.more}</div>}
          <div className="blog-grid">
            {shown.map((p) => (
              <div key={p.slug} className="reveal"><PostCard post={p} lang={lang} ui={ui} /></div>
            ))}
          </div>
        </div>
      </section>

      <Contact t={t} />
      <Footer t={t} />
    </>
  );
}

// ── Parseur de liens Markdown inline [texte](url) → JSX ─────────────
// Permet d'écrire des liens dans blog-data.js sans casser la structure JSON.
// Lien interne (relatif ou /chemin) : ouvre dans l'onglet courant.
// Lien externe (http*) : nouvelle fenêtre + rel sécurisé.
function renderInline(text) {
  if (!text || typeof text !== "string") return text;
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  const out = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const label = m[1];
    const href = m[2];
    const isExternal = /^https?:\/\//i.test(href);
    out.push(
      isExternal
        ? <a key={key++} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
        : <a key={key++} href={href}>{label}</a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length === 1 && typeof out[0] === "string" ? text : out;
}

// ── Rendu d'un bloc de corps d'article ──────────────────────────────
function ArticleBlock({ block }) {
  if (block.type === "h2") return <h2 className="display article-h2">{block.text}</h2>;
  if (block.type === "quote") return (
    <blockquote className="article-quote">
      <span className="article-quote-mark" aria-hidden="true">"</span>
      <span className="italic-serif">{block.text}</span>
    </blockquote>
  );
  if (block.type === "ul") return (
    <ul className="article-list">{block.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}</ul>
  );
  if (block.type === "ol") return (
    <ol className="article-list article-list-ol">{block.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}</ol>
  );
  return <p className="article-p">{renderInline(block.text)}</p>;
}

// ── Lecteur d'article ───────────────────────────────────────────────
function ArticlePage({ t, lang, slug }) {
  useReveal();
  const ui = BLOG_UI[lang] || BLOG_UI.fr;
  const post = getPost(slug);

  useEffect(() => {
    if (post) document.title = post[lang].title + " — VIDALOZZI";
    // Auto-publication : noindex tant que l'article n'est pas à sa date de sortie
    if (post) setRobots(isPublished(post));
    return () => setRobots(true);
  }, [post, lang]);

  if (post && !isPublished(post)) {
    const c0 = post[lang];
    const liveStr = fmtDate(post.dateISO, lang);
    const day = new Date(post.dateISO + "T00:00:00").toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", { day: "numeric", month: "long", year: "numeric" });
    return (
      <>
        <section className="article" style={{ minHeight: "70vh" }}>
          <div className="wrap-tight">
            <div className="article-top reveal"><a href="blog.html" className="article-back">{ui.back}</a></div>
            <header className="article-head reveal">
              <div className="blog-meta" style={{ justifyContent: "center" }}>
                <span className="tag"><span className="tag-dot" />{post.category[lang]}</span>
                <span className="mono blog-meta-time">{lang === "en" ? "Coming soon" : "À paraître"}</span>
              </div>
              <h1 className="display article-title">{c0.title}</h1>
              <p className="article-dek italic-serif">{lang === "en" ? "This article publishes on " + day + "." : "Cet article paraît le " + day + "."}</p>
              <a className="btn-ghost" href="blog.html" style={{ marginTop: 8 }}>{ui.notFoundLink}</a>
            </header>
          </div>
        </section>
        <Footer t={t} />
      </>
    );
  }

  if (!post) {
    return (
      <section style={{ minHeight: "70vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
        <p className="display" style={{ fontSize: 40 }}>{ui.notFound}</p>
        <a className="btn-ghost" href="blog.html">{ui.notFoundLink}</a>
      </section>
    );
  }

  const c = post[lang];
  const cat = post.category[lang];
  const related = publishedPosts().filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <article className="article">
        <div className="wrap-tight">
          <div className="article-top reveal">
            <a href="blog.html" className="article-back">{ui.back}</a>
          </div>
          <header className="article-head reveal">
            <div className="blog-meta" style={{ justifyContent: "center" }}>
              <span className="tag"><span className="tag-dot" />{cat}</span>
              <span className="mono blog-meta-time">{fmtDate(post.dateISO, lang)} · {post.readMin} {ui.minRead}</span>
            </div>
            <h1 className="display article-title">{c.title}</h1>
            <p className="article-dek italic-serif">{c.dek}</p>
            <div className="article-byline">
              <span className="article-byline-dot" />
              {ui.by}
            </div>
          </header>
        </div>

        <div className="wrap reveal">
          <div className="ph article-hero">
            <img className="blog-cover-img" src={"assets/blog/" + post.slug + ".jpg"} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <span className="ph-label">{post.hero}</span>
            <span className="ph-coords">VIDALOZZI · JOURNAL</span>
          </div>
        </div>

        <div className="wrap-tight">
          <div className="article-body">
            {c.body.map((b, i) => <ArticleBlock key={i} block={b} />)}
          </div>

          <div className="article-cta reveal">
            <div>
              <div className="eyebrow" style={{ marginBottom: 14 }}>— {ui.eyebrow}</div>
              <h3 className="display article-cta-head">{ui.ctaHead}</h3>
              <p className="blog-dek" style={{ margin: "0 0 28px" }}>{ui.ctaSub}</p>
            </div>
            <a className="btn-primary" href="https://calendly.com/vidalozzi" target="_blank" rel="noreferrer">
              <span className="nav-cta-dot" style={{ background: "var(--bg-deep)" }} />
              {ui.ctaBtn}
              <span style={{ marginLeft: "auto" }}>→</span>
            </a>
          </div>
        </div>
      </article>

      <section className="blog-grid-sec" style={{ borderTop: "0.5px solid var(--line)" }} hidden={related.length === 0}>
        <div className="wrap">
          <div className="eyebrow reveal" style={{ marginBottom: 28 }}>— {ui.related}</div>
          <div className="blog-grid">
            {related.map((p) => (
              <div key={p.slug} className="reveal"><PostCard post={p} lang={lang} ui={ui} /></div>
            ))}
          </div>
        </div>
      </section>

      <Footer t={t} />
    </>
  );
}

Object.assign(window, { BlogPage, ArticlePage });


/* ── app.jsx ─────────────────────────────────────────── */

// VIDALOZZI — App shell
// (React destructuring hoisted to bundle preamble)
const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accent": "#C9A87C",
  "fontDisplay": "DM Serif Display",
  "fontBody": "Montserrat",
  "density": "regular",
  "dark": true,
  "imgMode": "color"
}/*EDITMODE-END*/;

const FONT_OPTIONS_DISPLAY = [
  { value: "DM Serif Display", label: "DM Serif" },
  { value: "Bodoni Moda", label: "Bodoni" },
  { value: "Playfair Display", label: "Playfair" },
  { value: "Cormorant Garamond", label: "Cormorant" },
];
const FONT_OPTIONS_BODY = [
  { value: "Montserrat", label: "Montserrat" },
  { value: "Inter", label: "Inter" },
  { value: "Manrope", label: "Manrope" },
  { value: "DM Sans", label: "DM Sans" },
];

function App({ page, slug }) {
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const [lang, setLang] = useState(localStorage.getItem("vz_lang") || "fr");
  const isDesktop = () => window.matchMedia("(min-width: 861px)").matches;
  const [isDesktopState, setIsDesktopState] = useState(() => isDesktop());
  const [editMode, setEditMode] = useState(() => isDesktop() && localStorage.getItem("vz_edit") === "1");
  useEffect(() => { localStorage.setItem("vz_lang", lang); }, [lang]);

  // ── Premium FX (batch 1 + 2) ──────────────────────────────────────
  useLenis();
  useMagneticButtons(".btn-primary, .nav-cta");
  useSpotlightTilt(".spotlight-tilt", 5);

  // Force off when shrinking past the desktop breakpoint
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 861px)");
    const onChange = () => {
      setIsDesktopState(mq.matches);
      if (!mq.matches) {
        setEditMode(false);
        // Hard sweep: someone with localStorage flag from another device shouldn't get edit on mobile
        localStorage.removeItem("vz_edit");
      }
    };
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange); };
  }, []);

  // Toggle edit mode via URL hash (#edit) or keyboard shortcut (Cmd/Ctrl + Shift + E)
  // Desktop-only: visitors on phones/tablets can't enter edit mode.
  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === "#edit") {
        history.replaceState(null, "", window.location.pathname);
        if (!isDesktop()) return;
        localStorage.setItem("vz_edit", "1");
        setEditMode(true);
      } else if (window.location.hash === "#exit") {
        localStorage.removeItem("vz_edit");
        setEditMode(false);
        history.replaceState(null, "", window.location.pathname);
      }
    };
    checkHash();
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "e") {
        e.preventDefault();
        if (!isDesktop()) return;
        setEditMode((v) => {
          const next = !v;
          if (next) localStorage.setItem("vz_edit", "1");
          else localStorage.removeItem("vz_edit");
          return next;
        });
      }
    };
    window.addEventListener("hashchange", checkHash);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("hashchange", checkHash);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  // Apply tweaks to document
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = tweaks.dark ? "dark" : "light";
    root.dataset.density = tweaks.density;
    root.dataset.imgmode = tweaks.imgMode;
    root.dataset.editmode = editMode ? "1" : "0";
    root.style.setProperty("--accent", tweaks.accent);
    root.style.setProperty("--font-display", `"${tweaks.fontDisplay}"`);
    root.style.setProperty("--font-body", `"${tweaks.fontBody}"`);
    document.body.style.fontFamily = `"${tweaks.fontBody}", -apple-system, system-ui, sans-serif`;
  }, [tweaks, editMode]);

  const t = window.COPY[lang];
  const PAGES = { home: HomePage, ugc: UgcPage, talent: TalentPage };
  // blog.jsx n'est chargé que sur les pages du journal ; on l'ajoute si présent
  if (typeof window.BlogPage !== "undefined") { PAGES.blog = window.BlogPage; PAGES.article = window.ArticlePage; }
  const PageComp = PAGES[page] || HomePage;

  return (
    <div className="grain">
      <ScrollProgress />
      <Nav lang={lang} setLang={setLang} t={t} current={page} />
      <PageComp t={t} lang={lang} slug={slug} />
      <ModeSwitch current={page} t={t} />
      {editMode && (
        <div style={{
          position: "fixed", top: 84, left: "50%", transform: "translateX(-50%)",
          zIndex: 1000, display: "flex", alignItems: "center", gap: 10,
          padding: "8px 14px",
          background: "var(--accent)", color: "var(--bg-deep)",
          borderRadius: 999,
          fontFamily: "JetBrains Mono, monospace", fontSize: 10,
          letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600,
          boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--bg-deep)" }} />
          Edit mode
          <button onClick={() => { localStorage.removeItem("vz_edit"); setEditMode(false); }}
            style={{ marginLeft: 6, background: "rgba(14,11,8,0.15)", color: "var(--bg-deep)", border: 0, borderRadius: 999, padding: "3px 8px", fontFamily: "inherit", fontSize: 9, letterSpacing: "0.12em", cursor: "pointer" }}>
            Exit
          </button>
        </div>
      )}
      <TweaksPanel title="Tweaks">
        <TweakSection label="Couleur d'accent" />
        <TweakColor label="Accent" value={tweaks.accent} onChange={(v) => setTweak("accent", v)} />
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {["#C9A87C", "#B5895A", "#A86D3D", "#D4AF37", "#7A5C3E", "#E6C99B"].map((c) => (
            <button key={c} onClick={() => setTweak("accent", c)} style={{ width: 22, height: 22, borderRadius: 4, background: c, border: "0.5px solid rgba(0,0,0,0.15)" }} />
          ))}
        </div>
        <TweakSection label="Typographie" />
        <TweakSelect label="Display" value={tweaks.fontDisplay} options={FONT_OPTIONS_DISPLAY} onChange={(v) => setTweak("fontDisplay", v)} />
        <TweakSelect label="Body" value={tweaks.fontBody} options={FONT_OPTIONS_BODY} onChange={(v) => setTweak("fontBody", v)} />
        <TweakSection label="Apparence" />
        <TweakToggle label="Dark mode" value={tweaks.dark} onChange={(v) => setTweak("dark", v)} />
        <TweakRadio label="Densité" value={tweaks.density} options={["compact", "regular", "comfy"]} onChange={(v) => setTweak("density", v)} />
        <TweakRadio label="Images" value={tweaks.imgMode} options={[{ value: "color", label: "Couleur" }, { value: "bw", label: "B&W" }]} onChange={(v) => setTweak("imgMode", v)} />
        {isDesktopState && (
          <>
            <TweakSection label="Édition (privé)" />
            <TweakToggle label="Edit mode" value={editMode} onChange={(v) => { if (v && !isDesktop()) return; if (v) localStorage.setItem("vz_edit", "1"); else localStorage.removeItem("vz_edit"); setEditMode(v); }} />
            <TweakButton label="Réinitialiser tous les textes" onClick={() => { if (confirm("Réinitialiser tous les textes modifiés ?")) window.VZ_TEXT && window.VZ_TEXT.reset(); }} />
          </>
        )}
        <TweakSection label="Mode" />
        <TweakRadio label="Section" value={page === "article" ? "blog" : page} options={[{ value: "home", label: "Home" }, { value: "ugc", label: "UGC" }, { value: "talent", label: "Talent" }, { value: "blog", label: "Blog" }]} onChange={(v) => { window.location.href = v === "home" ? "index.html" : v + ".html"; }} />
      </TweaksPanel>
    </div>
  );
}

window.VIDALOZZI_App = App;


// ── Auto-mount ──────────────────────────────────────────────────────
(function () {
  function mount() {
    var root = document.getElementById("root");
    if (!root) return;
    var page = document.body.dataset.page || "home";
    var slug = document.body.dataset.slug || undefined;
    if (typeof window.VIDALOZZI_App !== "function") {
      console.error("[vidalozzi] App not ready; bundle likely failed to load.");
      return;
    }
    ReactDOM.createRoot(root).render(
      React.createElement(window.VIDALOZZI_App, { page: page, slug: slug })
    );
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }
})();
